import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './styles/Chatconversation.css';
import API_CONFIG from './config';
import MicrophoneModal from './MicrophoneModal';
import ChartRenderer from './MermaidChart/ChartRenderer';
import './styles/chart.css';
import Tools from './MermaidChart/Tools';
import ChatLimitPopup from './ChatLimitPopup';
import Geohublogo from './Images/Geohublogoo.png';
import Disclaimer from './Disclaimer';
import PromptGuideModal from './PromptGuideModal';
import { Link } from 'react-router-dom';
import { MathJaxContext } from 'better-react-mathjax';
import MarkdownWithMath from './MermaidChart/markdownwithmath';
import Icons from './Icons';
import './styles/Chatbot.css';
// import { CsvToHtmlTable } from 'react-csv-to-table';


// Import utility modules
import {
    convertPlotlyToChartJs,
    getChartTypeName,
    getSeriesNames,
    getDataPointCount
} from './utils/chartUtils';
import { processFileUpload, initializePDFJS } from './utils/fileHandlers';
import {
    createNewConversation,
    sendChatMessage,
    sendStopCommand,
    loadConversationFromUrl,
    buildRequestConfig,
    dispatchConversationEvent
} from './utils/apiServices';
import {
    processUserMessageContent,
    createUserMessage,
    createAssistantMessage,
    createErrorMessage,
    getEditableContent,
    updateMessageContent,
    removeFollowingAssistantMessages,
    extractMessageContent,
    copyMessageToClipboard,
    generateMessageId,
    updateConversationTitle
} from './utils/messageHandlers';
import {
    MATH_JAX_CONFIG,
    CHAT_LIMITS,
    LOCAL_STORAGE_KEYS,
    SESSION_STORAGE_KEYS,
    EXTERNAL_LINKS,
    MODE_TYPES,
    CHART_MODES,
    DISCLAIMER_EXPIRY_DAYS,
    SCROLL_BEHAVIOR,
    TYPING_ANIMATION
} from './utils/constants';
import { useDropzone } from 'react-dropzone';

const config = MATH_JAX_CONFIG;
const extractMermaidDiagrams = (markdownContent) => {
    const mermaidRegex = /```mermaid\n([\s\S]*?)\n```/g;
    const diagrams = [];
    let match;

    while ((match = mermaidRegex.exec(markdownContent)) !== null) {
        diagrams.push(match[1]);
    }

    return diagrams;
};

// CSV Table Component with expand/collapse functionality
const CSVTable = ({ csvData, fileName }) => {
    const [showAllRows, setShowAllRows] = React.useState(false);

    if (!csvData || typeof csvData !== 'string') {
        return null;
    }

    try {
        // Parse CSV data into rows
        const lines = csvData.trim().split('\n');
        if (lines.length === 0) return null;

        // Parse each line, handling quoted values that might contain commas
        const parseCSVLine = (line) => {
            const result = [];
            let current = '';
            let inQuotes = false;

            for (let i = 0; i < line.length; i++) {
                const char = line[i];

                if (char === '"') {
                    inQuotes = !inQuotes;
                } else if (char === ',' && !inQuotes) {
                    result.push(current.trim());
                    current = '';
                } else {
                    current += char;
                }
            }
            result.push(current.trim());
            return result;
        };

        const rows = lines.map(line => parseCSVLine(line));
        const headers = rows[0];
        const dataRows = rows.slice(1);

        const maxRowsToShow = 5;
        const shouldShowToggle = dataRows.length > maxRowsToShow;
        const rowsToDisplay = showAllRows ? dataRows : dataRows.slice(0, maxRowsToShow);

        // Styles for the table


        return (
            <div className="csv-table-container">
                <div className="csv-file-info">
                    <span className="csv-file-icon">📊</span>
                    <span className="csv-file-name">{fileName || 'CSV Data'}</span>
                </div>
                <div className="csv-table-scroll">
                    <table className="csv-table">
                        <thead>
                            <tr>
                                {headers.map((header, index) => (
                                    <th key={index}>
                                        {header.replace(/"/g, '')}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rowsToDisplay.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {row.map((cell, cellIndex) => (
                                        <td key={cellIndex}>
                                            {cell.replace(/"/g, '')}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {shouldShowToggle && (
                    <div>
                        <button
                            className="csv-toggle-button"
                            onClick={() => setShowAllRows(!showAllRows)}
                        >
                            {showAllRows ? 'Show Less' : `Show All (${dataRows.length} rows)`}
                        </button>
                        <div className="csv-row-info">
                            {showAllRows
                                ? `Total: ${dataRows.length} rows`
                                : `Showing ${Math.min(maxRowsToShow, dataRows.length)} of ${dataRows.length} rows`
                            }
                        </div>
                    </div>
                )}
            </div>
        );
    } catch (error) {
        console.error('Error parsing CSV data:', error);
        return (
            <div style={{
                padding: '12px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '4px',
                color: '#856404',
            }}>
                <strong>Error parsing CSV:</strong> {error.message}
            </div>
        );
    }
};

// CSV to HTML Table conversion function
const csvToHtmlTable = (csvData, fileName) => {
    return <CSVTable csvData={csvData} fileName={fileName} />;
};

// Function to detect and extract CSV patterns from text
const detectAndExtractCSV = (text) => {
    if (!text || typeof text !== 'string') return null;

    // Look for CSV patterns in code blocks
    const csvCodeBlockRegex = /```(?:csv)?\n((?:[^\n]*,.*\n){2,}[^\n]*,.*)\n```/gi;
    const csvMatches = [];
    let match;

    while ((match = csvCodeBlockRegex.exec(text)) !== null) {
        csvMatches.push({
            csvData: match[1].trim(),
            fullMatch: match[0],
            startIndex: match.index,
            endIndex: match.index + match[0].length
        });
    }

    // Also look for CSV-like patterns in plain text (at least 2 lines with commas)
    const lines = text.split('\n');
    let csvLikeStart = -1;
    let csvLikeEnd = -1;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.includes(',') && line.split(',').length > 1) {
            if (csvLikeStart === -1) {
                csvLikeStart = i;
            }
            csvLikeEnd = i;
        } else if (csvLikeStart !== -1 && csvLikeEnd - csvLikeStart >= 1) {
            // Found a CSV-like section
            const csvData = lines.slice(csvLikeStart, csvLikeEnd + 1).join('\n');
            if (csvData.split('\n').length >= 2) {
                csvMatches.push({
                    csvData: csvData,
                    fullMatch: csvData,
                    startIndex: 0,
                    endIndex: csvData.length,
                    isPlainText: true
                });
            }
            csvLikeStart = -1;
            csvLikeEnd = -1;
        } else {
            csvLikeStart = -1;
            csvLikeEnd = -1;
        }
    }

    // Check the last potential CSV section
    if (csvLikeStart !== -1 && csvLikeEnd - csvLikeStart >= 1) {
        const csvData = lines.slice(csvLikeStart, csvLikeEnd + 1).join('\n');
        if (csvData.split('\n').length >= 2) {
            csvMatches.push({
                csvData: csvData,
                fullMatch: csvData,
                startIndex: 0,
                endIndex: csvData.length,
                isPlainText: true
            });
        }
    }

    return csvMatches.length > 0 ? csvMatches : null;
};

const Chatconversation = ({ onNewChat, currentConversation, setCurrentConversation, userInput, setUserInput, setIsTyping, onConversationCreated, currentConversationId, setCurrentConversationId, handleToggleSidebar }) => {
    // State variables
    const [isLoading, setIsLoading] = useState(false);
    const [token, setToken] = useState(null);
    const [file, setFile] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showPrompt, setShowPrompt] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(true);
    const [isStreaming, setIsStreaming] = useState(false);
    const [userId, setUserId] = useState(null);
    const [isListening, setIsListening] = useState(false);
    const [isMicModalOpen, setIsMicModalOpen] = useState(false);
    const [transcriptionText, setTranscriptionText] = useState('');
    const [isGraphingMode, setIsGraphingMode] = useState(false);
    const [chartType, setChartType] = useState('line');
    const [chartData, setChartData] = useState(null);
    const [accumulatedChartData, setAccumulatedChartData] = useState('');
    const [isSupportMode, setIsSupportMode] = useState(false);
    const [isToolsMode, setIsToolsMode] = useState(false);
    const [chatCount, setChatCount] = useState(0);
    const [showChatLimitPopup, setShowChatLimitPopup] = useState(false);
    const [dropdownSelection, setDropdownSelection] = useState("");
    const [showDisclaimerPopup, setShowDisclaimerPopup] = useState(false);
    const [isChartAnalysisMode, setIsChartAnalysisMode] = useState(false);
    const [currentChartData, setCurrentChartData] = useState(null);
    const [currentPlotConfig, setCurrentPlotConfig] = useState(null);
    const [isPromptGuideOpen, setIsPromptGuideOpen] = useState(false);
    const [functionCallState, setFunctionCallState] = useState(null);
    const [currentFunction, setCurrentFunction] = useState(null);
    const [functionArguments, setFunctionArguments] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [tempFunctionMessages, setTempFunctionMessages] = useState([]);
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [isUserScrolling, setIsUserScrolling] = useState(false);
    const [copiedMessageId, setCopiedMessageId] = useState(null);
    const messagesContainerRef = useRef(null);
    const streamResponseRef = useRef('');
    const audioStreamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const webSocketRef = useRef(null);
    const audioContextRef = useRef(null);
    const responseCountRef = useRef(0);
    const freeResponseCountRef = useRef(0);
    const hasFetchedHistoryRef = useRef(false);
    const shouldScrollRef = useRef(true);
    const pingIntervalRef = useRef(null);
    const abortControllerRef = useRef(null);
    const lastScrollTop = useRef(0);
    const scrollTimeout = useRef(null);
    const [apiStatus, setApiStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(true);
    const [typewriterText, setTypewriterText] = useState('');
    const [typewriterIndex, setTypewriterIndex] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const isAuthenticatedRef = useRef(isAuthenticated); // Add ref to track latest isAuthenticated value

    // Words to cycle through in the typewriter effect
    const typewriterWords = ['Geotech', 'Dams', 'Bridges', 'Tunnels', 'Foundations', 'Slopes', 'Retaining Walls', 'Soil Mechanics', 'Rock Mechanics', 'Geophysics', 'Geology', 'Hydrology', 'Seismology', 'Geotechnical Engineering'];

    // Typewriter effect logic
    useEffect(() => {
        const currentWord = typewriterWords[typewriterIndex];
        const speed = isDeleting ? 50 : 150; // Faster when deleting

        const timer = setTimeout(() => {
            if (!isDeleting) {
                // Typing
                if (typewriterText.length < currentWord.length) {
                    setTypewriterText(currentWord.slice(0, typewriterText.length + 1));
                } else {
                    // Pause before deleting
                    setTimeout(() => setIsDeleting(true), 2000);
                }
            } else {
                // Deleting
                if (typewriterText.length > 0) {
                    setTypewriterText(currentWord.slice(0, typewriterText.length - 1));
                } else {
                    setIsDeleting(false);
                    setTypewriterIndex((prevIndex) => (prevIndex + 1) % typewriterWords.length);
                }
            }
        }, speed);

        return () => clearTimeout(timer);
    }, [typewriterText, typewriterIndex, isDeleting, typewriterWords]);

    // Update isAuthenticatedRef whenever isAuthenticated changes
    useEffect(() => {
        isAuthenticatedRef.current = isAuthenticated;
    }, [isAuthenticated]);

    // Function to fetch OpenAI API status
    const fetchApiStatus = async () => {
        try {
            const response = await fetch('https://status.openai.com/api/v2/status.json');
            if (!response.ok) throw new Error('Failed to fetch OpenAI status data');
            const data = await response.json();
            setApiStatus(data.status);
            setStatusLoading(false);
        } catch (err) {
            console.error('Error fetching OpenAI status:', err);
            setApiStatus(null);
            setStatusLoading(false);
        }
    };

    // Get status color based on indicator
    const getStatusColor = (indicator) => {
        switch (indicator) {
            case 'none':
                return '#10b981'; // green
            case 'minor':
                return '#f59e0b'; // yellow
            case 'major':
                return '#dc2626'; // red
            case 'critical':
                return '#8B0000'; // dark red
            default:
                return '#6b7280'; // gray
        }
    };

    // Get status text class based on indicator
    const getStatusTextClass = (indicator) => {
        switch (indicator) {
            case 'none':
                return 'operational';
            case 'minor':
                return 'degraded';
            case 'major':
                return 'outage';
            case 'critical':
                return 'critical';
            default:
                return 'unknown';
        }
    };

    // Get status text based on indicator
    const getStatusText = (indicator) => {
        switch (indicator) {
            case 'none':
                return 'Operational';
            case 'minor':
                return 'Degraded';
            case 'major':
                return 'Outage';
            case 'critical':
                return 'Critical';
            default:
                return 'Unknown';
        }
    };

    // Fetch status on component mount and set up interval
    useEffect(() => {
        fetchApiStatus();
        const interval = setInterval(fetchApiStatus, 30000); // Update every 30 seconds
        return () => clearInterval(interval);
    }, []);

    const handleNewMessage = useCallback((message) => {
        setCurrentConversation((prev) => {
            const exists = prev.find((m) => m.id === message.id);
            if (exists) {
                const updatedMessage = {
                    ...exists,
                    content: message.content,
                    ...(message.images ? { images: message.images } : {}),
                    ...(message.functionCall ? { functionCall: message.functionCall } : {}),
                    ...(message.searchStatus ? { searchStatus: message.searchStatus } : {})
                };
                return prev.map(m => m.id === message.id ? updatedMessage : m);
            }
            return [...prev, message];
        });

        // Only auto-scroll if user is not actively scrolling and we're near the bottom
        if (messagesContainerRef.current && !isUserScrolling) {
            const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
            const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

            if (isNearBottom) {
                shouldScrollRef.current = true;
            }
        }
    }, [setCurrentConversation, isUserScrolling]);

    // Debounced scroll function to prevent too frequent scroll calls during streaming
    const debouncedScrollRef = useRef(null);

    const scrollToBottom = useCallback(() => {
        if (messagesContainerRef.current && !isUserScrolling) {
            // Clear any pending scroll
            if (debouncedScrollRef.current) {
                clearTimeout(debouncedScrollRef.current);
            }

            // Debounce scroll calls during streaming for smoother experience
            debouncedScrollRef.current = setTimeout(() => {
                const { scrollHeight, clientHeight } = messagesContainerRef.current;

                // Use smooth scrolling for better UX during live responses
                messagesContainerRef.current.scrollTo({
                    top: scrollHeight - clientHeight,
                    behavior: 'smooth'
                });

                // Reset scroll flag after completion
                shouldScrollRef.current = false;
            }, 50); // Small delay to batch scroll calls
        }
    }, [isUserScrolling]);

    useEffect(() => {
        if (!isUserScrolling && shouldScrollRef.current) {
            // Use different scroll behavior based on whether we're streaming
            if (isStreaming) {
                // During streaming, use immediate scroll for better responsiveness
                if (messagesContainerRef.current) {
                    const { scrollHeight, clientHeight } = messagesContainerRef.current;
                    messagesContainerRef.current.scrollTop = scrollHeight - clientHeight;
                }
            } else {
                // For non-streaming updates, use smooth scroll
                scrollToBottom();
            }
        }
    }, [currentConversation, scrollToBottom, isUserScrolling, isStreaming]);

    useEffect(() => {
        if (currentConversation.length === 0) setShowPrompt(true);
    }, [currentConversation]);

    const handleScroll = useCallback(() => {
        if (messagesContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
            const currentScrollTop = scrollTop;

            if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

            // Improved scroll detection for long content
            const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;
            const isNearTop = scrollTop < 50;
            const isScrollingUp = currentScrollTop < lastScrollTop.current;

            if (isScrollingUp || isNearTop) {
                setIsUserScrolling(true);
            } else if (isNearBottom) {
                setIsUserScrolling(false);
            }

            lastScrollTop.current = currentScrollTop;

            scrollTimeout.current = setTimeout(() => {
                if (isNearBottom) {
                    setIsUserScrolling(false);
                }
            }, 2000);
        }
    }, []);

    useEffect(() => {
        const messagesContainer = messagesContainerRef.current;
        if (messagesContainer) {
            // Use passive scroll listener for better performance with long content
            messagesContainer.addEventListener('scroll', handleScroll, { passive: true });

            // Add wheel event listener for smooth mouse scrolling
            const handleWheelEvent = (event) => {
                // Only handle if we're not already at the top/bottom limits
                const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
                const isAtTop = scrollTop <= 0;
                const isAtBottom = scrollTop + clientHeight >= scrollHeight;

                // Allow natural scrolling at boundaries
                if ((event.deltaY < 0 && isAtTop) || (event.deltaY > 0 && isAtBottom)) {
                    return;
                }

                // Prevent default and handle scrolling
                event.preventDefault();

                // Clear any pending auto-scroll during user interaction
                if (debouncedScrollRef.current) {
                    clearTimeout(debouncedScrollRef.current);
                }

                // Calculate scroll amount with better sensitivity handling
                let scrollAmount = event.deltaY;

                // Adjust scroll sensitivity for different devices
                if (Math.abs(scrollAmount) < 10) {
                    scrollAmount *= 2; // Increase sensitivity for small movements
                }

                // Use immediate scroll for better responsiveness
                messagesContainer.scrollTop += scrollAmount;
            };

            messagesContainer.addEventListener('wheel', handleWheelEvent, { passive: false });

            return () => {
                messagesContainer.removeEventListener('scroll', handleScroll);
                messagesContainer.removeEventListener('wheel', handleWheelEvent);
                if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
            };
        }
    }, [handleScroll]);

    useEffect(() => {
        return () => {
            if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
            if (debouncedScrollRef.current) clearTimeout(debouncedScrollRef.current);
        };
    }, []);

    const dispatchConversationEvent = useCallback((conversation) => {
        if (!conversation) return;

        // Create a custom event with conversation data
        const event = new CustomEvent('conversationCreated', {
            detail: conversation
        });

        // Dispatch the event for the Sidebar to listen for
        window.dispatchEvent(event);

        // Also call the callback if provided
        if (onConversationCreated) {
            onConversationCreated(conversation);
        }
    }, [onConversationCreated]);

    const handleUpdateConversationTitle = (conversationId, title) => {
        return updateConversationTitle(conversationId, title, userId, (conversation) => {
            dispatchConversationEvent(conversation);
        });
    };

    // Restore initial title generation logic for new conversations
    const generateInitialTitle = useCallback((userMessage) => {
        if (!userMessage || userMessage.length === 0) return 'New Conversation';

        // Truncate to 30 characters for sidebar display
        const truncatedTitle = userMessage.length > 30
            ? userMessage.substring(0, 30) + '...'
            : userMessage;

        return truncatedTitle;
    }, []);

    const handleCreateNewConversation = async () => {
        setUploadedFile(null); // Clear attached file when starting a new chat

        // Reset the hasFetchedHistoryRef for new conversations
        hasFetchedHistoryRef.current = false;
        responseCountRef.current = 0;

        // Log new conversation creation attempt
        // console.log('🆕 NEW CONVERSATION:', {
        //     timestamp: new Date().toISOString(),
        //     isAuthenticated: isAuthenticated,
        //     userId: userId,
        //     currentConversationLength: currentConversation.length
        // });

        const result = await createNewConversation(isAuthenticated, userId);
        if (result) {
            // Log successful conversation creation
            // console.log('✅ CONVERSATION CREATED:', {
            //     timestamp: new Date().toISOString(),
            //     conversationId: result.id,
            //     isAuthenticated: isAuthenticated,
            //     userId: userId,
            //     title: result.conversation?.title || result.conversation?.conversations?.title || 'New Conversation'
            // });

            setCurrentConversationId(result.id);
            dispatchConversationEvent(result.conversation);
            return result.id;
        }

        // Log failed conversation creation
        // console.log('❌ CONVERSATION FAILED:', {
        //     timestamp: new Date().toISOString(),
        //     isAuthenticated: isAuthenticated,
        //     userId: userId
        // });

        return null;
    };

    useEffect(() => {
        const authToken = localStorage.getItem(LOCAL_STORAGE_KEYS.AUTH_TOKEN) || localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
        setToken(authToken);
        const isAuth = !!authToken;
        setIsAuthenticated(isAuth);
        const savedChatCount = parseInt(localStorage.getItem(LOCAL_STORAGE_KEYS.FREE_CHAT_COUNT) || '0', 10);
        setChatCount(savedChatCount);
        if (!isAuth && savedChatCount >= CHAT_LIMITS.MAX_FREE_CHATS) setShowChatLimitPopup(true);

        const disclaimerDataRaw = localStorage.getItem(LOCAL_STORAGE_KEYS.DISCLAIMER_SEEN);
        let hasValidDisclaimer = false;

        if (disclaimerDataRaw) {
            try {
                const disclaimerData = JSON.parse(disclaimerDataRaw);
                if (disclaimerData?.expiresAt) {
                    const now = new Date().getTime();
                    if (now < disclaimerData.expiresAt) {
                        hasValidDisclaimer = true;
                    } else {
                        localStorage.removeItem(LOCAL_STORAGE_KEYS.DISCLAIMER_SEEN);
                    }
                } else if (disclaimerDataRaw === 'true') {
                    hasValidDisclaimer = true;
                    const expirationDate = new Date();
                    expirationDate.setDate(expirationDate.getDate() + DISCLAIMER_EXPIRY_DAYS);
                    localStorage.setItem(LOCAL_STORAGE_KEYS.DISCLAIMER_SEEN, JSON.stringify({
                        acknowledged: true,
                        expiresAt: expirationDate.getTime()
                    }));
                }
            } catch (e) {
                if (disclaimerDataRaw === 'true') {
                    hasValidDisclaimer = true;
                    const expirationDate = new Date();
                    expirationDate.setDate(expirationDate.getDate() + DISCLAIMER_EXPIRY_DAYS);
                    localStorage.setItem(LOCAL_STORAGE_KEYS.DISCLAIMER_SEEN, JSON.stringify({
                        acknowledged: true,
                        expiresAt: expirationDate.getTime()
                    }));
                }
            }
        }

        if ((!isAuth || savedChatCount === 0) && !hasValidDisclaimer) setShowDisclaimerPopup(true);

        if (authToken) {
            try {
                const tokenParts = authToken.split('.');
                if (tokenParts.length === 3) {
                    const payload = JSON.parse(atob(tokenParts[1]));
                    if (payload?.user_id) setUserId(payload.user_id);
                }
            } catch (error) {
                console.error('Error extracting user ID from token:', error);
            }
        }
    }, []);

    const handleStreamCompletion = useCallback((conversationId) => {
        // console.log('🎯 HANDLE STREAM COMPLETION CALLED:', { 
        //     conversationId, 
        //     isAuthenticated: isAuthenticatedRef.current, 
        //     responseCountRef: responseCountRef.current,
        //     hasFetchedHistoryRef: hasFetchedHistoryRef.current,
        //     currentConversationId: currentConversationId
        // });

        if (isAuthenticatedRef.current && conversationId) {
            responseCountRef.current += 1;
            // console.log('📊 Response count updated:', responseCountRef.current);

            // Fetch updated title after the first AI response (when responseCount === 1)
            if (responseCountRef.current === 1 && !hasFetchedHistoryRef.current) {
                // console.log('🚀 Dispatching chatResponseComplete event for conversation:', conversationId);
                const event = new CustomEvent('chatResponseComplete', {
                    detail: { conversationId: conversationId, responseCount: responseCountRef.current }
                });
                window.dispatchEvent(event);
                hasFetchedHistoryRef.current = true;
                // console.log('✅ Event dispatched and hasFetchedHistoryRef set to true');
            } else {
                // console.log('⏭️ Skipping event dispatch - responseCount:', responseCountRef.current, 'hasFetchedHistory:', hasFetchedHistoryRef.current);
            }
        } else {
            // console.log('👤 Not authenticated or no conversationId, updating free response count');
            freeResponseCountRef.current += 1;

            if (freeResponseCountRef.current === 4) {
                const hasMermaidDiagrams = currentConversation.some(msg =>
                    typeof msg.content === 'string' && msg.content.includes('```mermaid')
                );

                if (hasMermaidDiagrams && !isAuthenticated && chatCount >= (CHAT_LIMITS.MAX_FREE_CHATS - 1)) {
                    setTimeout(() => setShowChatLimitPopup(true), 1000);
                }
            }
        }

        shouldScrollRef.current = true;
        scrollToBottom();
    }, []); // Empty dependency array since we're using refs which don't need dependencies

    // Modified handleFileChange function to support CSV, PDF, DOCX, and TXT parsing
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Clear any existing assistant info from localStorage when a new file is uploaded
        // clearAssistantInfo();
        // console.log('Cleared previous assistantInfo from localStorage due to new file upload');

        // Clear downloadedFileIds from localStorage when a new file is uploaded
        localStorage.removeItem('downloadedFileIds');
        // console.log('Cleared downloadedFileIds from localStorage due to new file upload');

        setIsUploading(true);

        try {
            if (file.type.startsWith('image/')) {
                // Store image file locally instead of uploading to Supabase
                setUploadedFile({
                    file: file,
                    file_name: file.name,
                    url: URL.createObjectURL(file),
                    type: 'image'
                });
            } else if (file.type === 'text/csv') {
                // CSV handling code
                const reader = new FileReader();
                reader.onload = async (event) => {
                    const csvData = event?.target?.result;
                    setUploadedFile({
                        file: file,
                        file_name: file.name,
                        type: 'csv',
                        content: csvData
                    });
                    setIsUploading(false);
                };
                reader.readAsText(file);
            } else if (file.type === 'text/plain') {
                // TXT handling code
                const reader = new FileReader();
                reader.onload = async (event) => {
                    const txtData = event?.target?.result;
                    setUploadedFile({
                        file: file,
                        file_name: file.name,
                        type: 'txt',
                        content: txtData
                    });
                    setIsUploading(false);
                };
                reader.readAsText(file);
            } else if (file.type === 'application/pdf') {
                // PDF handling code
                setUploadedFile({
                    file: file,
                    file_name: file.name,
                    type: 'pdf'
                });
                setIsUploading(false);
            } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
                // DOCX handling code
                setUploadedFile({
                    file: file,
                    file_name: file.name,
                    type: 'docx'
                });
                setIsUploading(false);
            }
        } catch (error) {
            console.error('Error handling file:', error);
            alert(error.message || 'Error uploading file');
            e.target.value = '';
        } finally {
            setIsUploading(false);
        }
    };

    const handleSendMessage = async () => {
        if (!userInput.trim() || isLoading || isUploading) return;

        if (!isAuthenticated && chatCount >= CHAT_LIMITS.MAX_FREE_CHATS) {
            setShowChatLimitPopup(true);
            return;
        }

        setIsLoading(true);
        abortControllerRef.current = new AbortController();
        const isNewConversation = currentConversation.length === 0;
        const userMessageId = `user-${Date.now()}`;
        const assistantMessageId = `assistant-${Date.now()}`;

        try {
            if (!isAuthenticated) {
                const newChatCount = chatCount + 1;
                setChatCount(newChatCount);
                localStorage.setItem(LOCAL_STORAGE_KEYS.FREE_CHAT_COUNT, newChatCount.toString());
                if (newChatCount === CHAT_LIMITS.MAX_FREE_CHATS) {
                    setTimeout(() => setShowChatLimitPopup(true), 1000);
                }
            }

            let conversationIdToUse = currentConversationId;
            if (isAuthenticated && !conversationIdToUse) {
                const newConversationId = await handleCreateNewConversation();
                if (!newConversationId) throw new Error('Failed to create conversation. Please try again.');
                conversationIdToUse = newConversationId;
                // console.log('🆔 Set conversationIdToUse to:', conversationIdToUse);
            }

            const trimmedInput = userInput.trim();

            // Generate initial title for new conversations when user sends first message
            if (isNewConversation && isAuthenticated && conversationIdToUse) {
                const initialTitle = generateInitialTitle(trimmedInput);

                // Don't dispatch another conversationCreated event - it's already done in handleCreateNewConversation
                // Instead, we'll let the chatResponseComplete event handle the title update after AI response
                // console.log('📝 Generated initial title for new conversation:', initialTitle);
            }

            let userMessageContent;
            if (isGraphingMode && uploadedFile && uploadedFile.type === 'csv') {
                userMessageContent = `Generate chart from uploaded CSV data: ${uploadedFile.file_name}`;
            } else if (isGraphingMode && isChartAnalysisMode && currentChartData && currentPlotConfig) {
                userMessageContent = `Analyze chart: ${trimmedInput}`;
            } else if (uploadedFile && uploadedFile.type === 'image') {
                userMessageContent = { text: trimmedInput, image: uploadedFile.file_name };
            } else if (uploadedFile && uploadedFile.type === 'pdf') {
                userMessageContent = `${trimmedInput} [PDF Content: ${uploadedFile.file_name}]`;
            } else if (uploadedFile && uploadedFile.type === 'docx') {
                userMessageContent = `${trimmedInput} [DOCX Content: ${uploadedFile.file_name}]`;
            } else if (uploadedFile && uploadedFile.type === 'csv') {
                userMessageContent = {
                    text: trimmedInput,
                    csvData: uploadedFile.content,
                    fileName: uploadedFile.file_name
                };
            } else if (uploadedFile && uploadedFile.type === 'txt') {
                userMessageContent = {
                    text: trimmedInput,
                    txtData: uploadedFile.content,
                    fileName: uploadedFile.file_name
                };
            } else {
                userMessageContent = trimmedInput;
            }

            const userMessage = {
                id: userMessageId,
                role: 'user',
                content: userMessageContent,
                created_at: new Date().toISOString(),
            };

            setCurrentConversation(prev => [...prev, userMessage]);
            setUserInput('');
            setShowPrompt(false);
            setUploadedFile(null);

            if (isGraphingMode && !isChartAnalysisMode && (!uploadedFile || uploadedFile.type !== 'csv')) {
                handleNewMessage({
                    id: assistantMessageId,
                    role: 'assistant',
                    content: 'Please upload a CSV file first to generate a chart.',
                    created_at: new Date().toISOString(),
                });
                setIsLoading(false);
                return;
            }

            handleNewMessage({
                id: assistantMessageId,
                role: 'assistant',
                content: '',
                created_at: new Date().toISOString(),
            });

            let endpoint;
            let requestBody;
            let headers = { 'Content-Type': 'application/json' };

            if (isAuthenticated && token) headers['Authorization'] = `Bearer ${token}`;

            if (isGraphingMode) {
                endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.GRAPH}`;

                if (isChartAnalysisMode && currentChartData && currentPlotConfig) {
                    // Chart analysis mode
                    requestBody = {
                        mode: 'analyze',
                        query: trimmedInput,
                        chartData: currentChartData,
                        plotConfig: currentPlotConfig
                    };
                } else {
                    // Chart generation mode
                    requestBody = {
                        mode: 'generate',
                        csvData: uploadedFile?.content,
                        chartType: chartType
                    };
                }
            } else if (uploadedFile && uploadedFile.type === 'image') {
                endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.IMAGE}`;
                
                // Use FormData for image uploads
                const formData = new FormData();
                formData.append('query', userInput.trim());
                formData.append('file', uploadedFile.file);
                if (isAuthenticated && conversationIdToUse) {
                    formData.append('conversation_id', conversationIdToUse);
                }
                
                headers = {}; // Let browser set correct Content-Type for FormData
                requestBody = formData;
            } else if (uploadedFile && (uploadedFile.type === 'pdf' || uploadedFile.type === 'csv' || uploadedFile.type === 'txt' || uploadedFile.type === 'docx')) {
                // Use FormData for PDF, CSV, TXT, and DOCX uploads
                const formData = new FormData();
                formData.append('query', userInput.trim());
                formData.append('file', uploadedFile.file);

                if (isAuthenticated) {
                    endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.CHAT}`;
                    formData.append('conversation_id', conversationIdToUse);
                } else {
                    endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.CUSTOM_CHAT}`;
                }

                headers = {}; // Let browser set correct Content-Type for FormData
                requestBody = formData;
            } else {
                const assistant = localStorage.getItem('assistantInfo');
                const assistantInfo = assistant ? JSON.parse(assistant) : null;
                if (isAuthenticated) {
                    endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.CHAT}`;

                    requestBody = JSON.stringify({
                        query: typeof userMessageContent === 'string' ? userMessageContent : userMessageContent.text,
                        conversation_id: conversationIdToUse,
                        assistant_id: assistantInfo?.assistantId || '',
                        file_id: assistantInfo?.fileId || ''
                    });
                    headers = {
                        'Content-Type': 'application/json'
                    };
                } else {
                    endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.CUSTOM_CHAT}`;
                    requestBody = JSON.stringify({
                        query: typeof userMessageContent === 'string' ? userMessageContent : userMessageContent.text
                    });
                    headers = {
                        'Content-Type': 'application/json'
                    };
                }
            }

            let response;
            if (isAuthenticated && token) {
                // Use the authenticated API client for authenticated users
                const { apiClient } = await import('./utils/apiClient');
                response = await apiClient.request(endpoint, {
                    method: 'POST',
                    headers: headers,
                    body: requestBody,
                    signal: abortControllerRef.current.signal
                });
            } else {
                // Use regular fetch for unauthenticated users
                response = await fetch(endpoint, {
                    method: 'POST',
                    headers: headers,
                    body: requestBody,
                    signal: abortControllerRef.current.signal
                });
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail?.[0]?.msg || errorData.error || 'Failed to process request');
            }

            if (isGraphingMode && !isChartAnalysisMode) {
                // Handle Plotly.js graph response (generation mode)
                try {
                    const data = await response.json();

                    // Check if we have a valid plot configuration
                    if (!data.plotConfig || !data.plotConfig.data) {
                        throw new Error('Invalid plot configuration received from server');
                    }

                    const plotConfig = data.plotConfig;

                    // Store chart data and config for potential future analysis
                    setCurrentChartData(data.chartData || []);
                    setCurrentPlotConfig(plotConfig);

                    // Enable chart analysis mode after generating a chart
                    setIsChartAnalysisMode(true);

                    // Convert the Plotly.js configuration to a Chart.js configuration
                    const chartConfig = convertPlotlyToChartJs(plotConfig);

                    // Check if there is actual data to display
                    const hasData = chartConfig.data.datasets &&
                        chartConfig.data.datasets.length > 0 &&
                        chartConfig.data.datasets.some(ds => ds.data && ds.data.length > 0);

                    if (!hasData) {
                        handleNewMessage({
                            id: assistantMessageId,
                            role: 'assistant',
                            content: 'Unable to generate a meaningful chart from the provided data. Please check your CSV format and try again.',
                            created_at: new Date().toISOString(),
                        });
                    } else {
                        // Create a message with the chart
                        handleNewMessage({
                            id: assistantMessageId,
                            role: 'assistant',
                            content: {
                                type: 'chart',
                                chartConfig: chartConfig,
                                title: plotConfig.layout?.title || 'Data Visualization',
                                chartType: getChartTypeName(plotConfig),
                                series: getSeriesNames(plotConfig),
                                dataPoints: getDataPointCount(plotConfig)
                            },
                            created_at: new Date().toISOString(),
                        });

                        // Add a hint that the user can now ask questions about the chart
                        setTimeout(() => {
                            handleNewMessage({
                                id: `assistant-hint-${Date.now()}`,
                                role: 'assistant',
                                content: "You can now ask questions about this chart to analyze the data further.",
                                created_at: new Date().toISOString(),
                            });
                        }, 500);
                    }
                    setUploadedFile(null);
                } catch (e) {
                    console.error('Error handling chart data:', e);
                    handleNewMessage({
                        id: assistantMessageId,
                        role: 'assistant',
                        content: `Error generating chart: ${e.message}. Please ensure your CSV data is properly formatted and try again.`,
                        created_at: new Date().toISOString(),
                    });
                    setIsChartAnalysisMode(false);
                }
            } else if (uploadedFile && (uploadedFile.type === 'csv' || uploadedFile.type === 'txt') && !isGraphingMode) {
                // Handle CSV/TXT Analysis response (now integrated with regular streaming)
                await handleStreamingResponse(response, assistantMessageId, conversationIdToUse);
                setUploadedFile(null);
            } else if (isGraphingMode && isChartAnalysisMode) {
                // Handle chart analysis response (streaming) - use main handleStreamingResponse
                await handleStreamingResponse(response, assistantMessageId, conversationIdToUse);
                setUploadedFile(null);
            } else {
                // Handle chat response (streaming) - use main handleStreamingResponse
                await handleStreamingResponse(response, assistantMessageId, conversationIdToUse);
            }
        } catch (error) {
            if (error.name === 'AbortError' || error.message?.includes('aborted') || error.message?.includes('cancelled by user')) {
                // console.log('Request was cancelled by user');
                // Clear any temporary function messages when cancelled
                setTempFunctionMessages([]);
                // Clear function call status from the last assistant message
                setCurrentConversation(prev => {
                    const lastAssistantIndex = prev.length - 1;
                    if (lastAssistantIndex >= 0 && prev[lastAssistantIndex].role === 'assistant') {
                        const updatedConversation = [...prev];
                        updatedConversation[lastAssistantIndex] = {
                            ...updatedConversation[lastAssistantIndex],
                            functionCall: null,
                            searchStatus: null
                        };
                        return updatedConversation;
                    }
                    return prev;
                });
                // Don't show any error message to user - just clean stop
                return;
            } else {
                console.error("Error:", error);
                handleNewMessage({
                    id: assistantMessageId,
                    role: 'assistant',
                    content: error.message || 'Failed to process request',
                    created_at: new Date().toISOString(),
                });
            }
        } finally {
            setIsLoading(false);
            setIsStreaming(false);
            abortControllerRef.current = null;
        }
    };


    const handleNewChatClick = () => {
        window.history.pushState({}, '', '/');
        setCurrentConversation([]);
        setCurrentConversationId(null);
        setUserInput('');
        setShowPrompt(true);
        setIsChartAnalysisMode(false);
        setCurrentChartData(null);
        setCurrentPlotConfig(null);
        responseCountRef.current = 0;
        hasFetchedHistoryRef.current = false;
        clearUploadedFile(); // Use the function that clears both state and file input
        window.dispatchEvent(new CustomEvent('clearConversation'));
        onNewChat();

        setTimeout(() => {
            shouldScrollRef.current = true;
            scrollToBottom();
            const textarea = document.getElementById("userTextarea");
            if (textarea) textarea.value = '';

            // Reset scroll behavior for new chat
            if (messagesContainerRef.current) {
                messagesContainerRef.current.scrollTop = 0;
            }
        }, 100);
    };

    const handleCopy = (text) => {
        setUserInput(text);
        document.getElementById("userTextarea").focus();
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey && !isLoading) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleWheel = (event) => {
        // Only handle wheel events if we're on the messages container
        if (messagesContainerRef.current && event.currentTarget === messagesContainerRef.current) {
            // Prevent default scroll behavior to avoid conflicts
            event.preventDefault();

            const container = messagesContainerRef.current;
            const scrollAmount = event.deltaY;

            // Clear any pending auto-scroll during user interaction
            if (debouncedScrollRef.current) {
                clearTimeout(debouncedScrollRef.current);
            }

            // Use immediate scroll for better responsiveness with mouse wheel
            container.scrollTop += scrollAmount;
        }
    };

    // Function to play base64 audio
    const playAudioResponse = async (base64Audio) => {
        try {
            // Create AudioContext if it doesn't exist
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
                    sampleRate: 16000
                });
            }

            // Decode base64 to Int16Array
            const binaryString = atob(base64Audio);
            const len = binaryString.length;
            const bytes = new Int16Array(len / 2);

            // Convert binary string to Int16Array
            for (let i = 0; i < len; i += 2) {
                bytes[i / 2] = (binaryString.charCodeAt(i) << 8) | binaryString.charCodeAt(i + 1);
            }

            // Convert Int16Array to Float32Array for AudioBuffer
            const float32Data = new Float32Array(bytes.length);
            for (let i = 0; i < bytes.length; i++) {
                float32Data[i] = bytes[i] / 32768.0;  // Convert Int16 to Float32
            }

            // Create AudioBuffer
            const audioBuffer = audioContextRef.current.createBuffer(
                1,  // Number of channels (mono)
                float32Data.length,
                audioContextRef.current.sampleRate
            );

            // Copy the float32Data to the AudioBuffer
            audioBuffer.copyToChannel(float32Data, 0);

            // Create and play AudioBufferSourceNode
            const source = audioContextRef.current.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(audioContextRef.current.destination);
            source.start(0);

        } catch (error) {
            console.error('Error playing audio:', error);
        }
    };

    // Function to convert Float32Array to PCM16 ArrayBuffer
    const floatTo16BitPCM = (float32Array) => {
        const buffer = new ArrayBuffer(float32Array.length * 2);
        const view = new DataView(buffer);
        let offset = 0;
        for (let i = 0; i < float32Array.length; i++, offset += 2) {
            let s = Math.max(-1, Math.min(1, float32Array[i]));
            view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
            ;
        }
        return buffer;
    };

    // Function to convert Float32Array to base64-encoded PCM16 data
    const base64EncodeAudio = (float32Array) => {
        const arrayBuffer = floatTo16BitPCM(float32Array);
        let binary = '';
        const bytes = new Uint8Array(arrayBuffer);
        const chunkSize = 0x8000; // 32KB chunks
        for (let i = 0; i < bytes.length; i += chunkSize) {
            const chunk = bytes.subarray(i, i + chunkSize);
            binary += String.fromCharCode.apply(null, chunk);
        }
        return btoa(binary);
    };

    // Replace the entire handleMicClick function
    const handleMicClick = async () => {
        setIsMicModalOpen(true);

        if (isListening) {
            setIsListening(false);
            setIsMicModalOpen(false);

            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }

            if (audioStreamRef.current) {
                audioStreamRef.current.getTracks().forEach(track => track.stop());
                audioStreamRef.current = null;
            }

            if (webSocketRef.current?.readyState === WebSocket.OPEN) {
                try {
                    webSocketRef.current.send(JSON.stringify({ type: 'input_audio_buffer.commit' }));
                    webSocketRef.current.close();
                } catch (error) {
                    console.error('Error closing WebSocket:', error);
                }
                webSocketRef.current = null;
            }

            if (audioContextRef.current) {
                audioContextRef.current.close();
                audioContextRef.current = null;
            }

            // Clear ping interval if exists
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
                pingIntervalRef.current = null;
            }

            return;
        }

        try {
            // // console.log('Starting voice chat initialization at', new Date().toISOString());
            setTranscriptionText('');

            // Get auth token first before creating any resources
            const authToken = localStorage.getItem('authToken');

            // Create AudioContext with proper sample rate
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
                sampleRate: 24000
            });

            // Resume AudioContext to ensure playback is allowed
            if (audioContextRef.current.state === 'suspended') {
                await audioContextRef.current.resume();
                // // console.log('AudioContext resumed at', new Date().toISOString());
            }

            let audioBufferQueue = [];
            let isPlaying = false;

            const decodeAndPlayAudio = (base64Audio) => {
                try {
                    // // console.log('Decoding audio data at', new Date().toISOString());
                    const cleanBase64 = base64Audio.replace(/\s+/g, '');
                    if (!/^[A-Za-z0-9+/=]+$/.test(cleanBase64)) {
                        throw new Error('Invalid base64 string');
                    }

                    const binaryString = atob(cleanBase64);
                    const bytes = new Uint8Array(binaryString.length);
                    for (let i = 0; i < binaryString.length; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                    }

                    const audioData = new Int16Array(bytes.buffer);
                    const audioBuffer = audioContextRef.current.createBuffer(
                        1,
                        audioData.length,
                        audioContextRef.current.sampleRate
                    );

                    const floatData = new Float32Array(audioData.length);
                    for (let i = 0; i < audioData.length; i++) {
                        floatData[i] = audioData[i] / 32768;
                    }
                    audioBuffer.copyToChannel(floatData, 0);

                    audioBufferQueue.push(audioBuffer);
                    // // console.log('Added audio buffer to queue, Queue length:', audioBufferQueue.length);

                    if (!isPlaying) {
                        playNextBuffer();
                    }
                } catch (error) {
                    console.error('Error decoding audio:', error);
                }
            };

            const playNextBuffer = () => {
                if (audioBufferQueue.length === 0) {
                    isPlaying = false;
                    // // console.log('Audio playback ended');
                    return;
                }

                isPlaying = true;
                const buffer = audioBufferQueue.shift();
                // // console.log('Playing audio buffer, Remaining queue length:', audioBufferQueue.length);

                // Safety check in case audioContextRef was closed
                if (!audioContextRef.current) {
                    console.error('AudioContext is null, cannot play audio');
                    return;
                }

                const source = audioContextRef.current.createBufferSource();
                source.buffer = buffer;
                source.connect(audioContextRef.current.destination);
                source.onended = () => {
                    // // console.log('Audio buffer playback finished');
                    playNextBuffer();
                };
                source.start(0);
            };

            const resampleAudio = (inputData, inputSampleRate) => {
                return new Promise((resolve) => {
                    const offlineContext = new OfflineAudioContext(
                        1,
                        Math.ceil((inputData.length * 24000) / inputSampleRate),
                        24000
                    );
                    const buffer = offlineContext.createBuffer(1, inputData.length, inputSampleRate);
                    buffer.copyToChannel(inputData, 0);

                    const source = offlineContext.createBufferSource();
                    source.buffer = buffer;
                    source.connect(offlineContext.destination);
                    source.start();

                    offlineContext.startRendering().then((renderedBuffer) => {
                        const resampledData = renderedBuffer.getChannelData(0);
                        resolve(resampledData);
                    });
                });
            };

            const base64EncodeAudio = (floatArray) => {
                const buffer = new ArrayBuffer(floatArray.length * 2);
                const view = new DataView(buffer);
                for (let i = 0; i < floatArray.length; i++) {
                    const s = Math.max(-1, Math.min(1, floatArray[i]));
                    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
                }
                const binary = String.fromCharCode.apply(null, new Uint8Array(buffer));
                return btoa(binary);
            };

            // // console.log('Getting audio media stream...');
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    sampleRate: 24000,
                    sampleSize: 16,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: false
                }
            });

            audioStreamRef.current = stream;

            let wsUrl = `ws://${API_CONFIG.BASE_URL.replace('http://', '')}/backend-api/conversations/audio`;

            // Append authentication token as query parameter if available
            if (authToken) {
                wsUrl += `?token=${encodeURIComponent(authToken)}`;
            }

            const ws = new WebSocket(wsUrl);
            ws.binaryType = 'arraybuffer';

            const connectionTimeoutId = setTimeout(() => {
                if (ws.readyState !== WebSocket.OPEN) {
                    console.error('WebSocket connection timed out after 10 seconds');
                    alert('Could not establish WebSocket connection. The server might not be available.');
                    setIsListening(false);
                    setIsMicModalOpen(false);

                    // Clean up resources
                    if (audioStreamRef.current) {
                        audioStreamRef.current.getTracks().forEach(track => track.stop());
                        audioStreamRef.current = null;
                    }

                    if (audioContextRef.current) {
                        audioContextRef.current.close();
                        audioContextRef.current = null;
                    }
                }
            }, 10000);

            // Set up ping interval to keep connection alive
            const pingInterval = setInterval(() => {
                if (ws && ws.readyState === WebSocket.OPEN) {
                    // // console.log('Sending ping to keep WebSocket alive');
                    try {
                        ws.send(JSON.stringify({ type: 'ping' }));
                    } catch (err) {
                        console.error('Error sending ping:', err);
                    }
                } else {
                    // // console.log('WebSocket not open for ping, current state:',
                    //     ws ? ws.readyState : 'WebSocket is null');
                    if (pingIntervalRef.current) {
                        clearInterval(pingIntervalRef.current);
                        pingIntervalRef.current = null;
                    }
                }
            }, 10000);

            // Store the interval reference
            pingIntervalRef.current = pingInterval;

            // Handle WebSocket events
            ws.onopen = () => {
                // // console.log('WebSocket connection established');

                // Clear the connection timeout
                clearTimeout(connectionTimeoutId);

                // Send initial authentication message
                if (authToken) {
                    try {
                        ws.send(JSON.stringify({
                            type: 'authenticate',
                            token: authToken
                        }));
                        // // console.log('Sent authentication message');
                    } catch (error) {
                        console.error('Error sending authentication message:', error);
                    }
                }

                // Set listening flag after successful connection
                setIsListening(true);

                // Set up audio processing after WebSocket connection is established
                const sourceNode = audioContextRef.current.createMediaStreamSource(stream);
                const processorNode = audioContextRef.current.createScriptProcessor(4096, 1, 1);

                sourceNode.connect(processorNode);
                processorNode.connect(audioContextRef.current.destination);

                processorNode.onaudioprocess = async (e) => {
                    if (webSocketRef.current?.readyState === WebSocket.OPEN) {
                        try {
                            const inputData = e.inputBuffer.getChannelData(0);
                            const inputSampleRate = e.inputBuffer.sampleRate;

                            const resampledData = await resampleAudio(inputData, inputSampleRate);
                            const base64Chunk = base64EncodeAudio(resampledData);
                            webSocketRef.current.send(base64Chunk);
                            // Reduced logging to avoid console spam
                        } catch (error) {
                            console.error('Error processing audio:', error);
                        }
                    }
                };
            };

            ws.onmessage = async (event) => {
                try {
                    const data = JSON.parse(event.data);

                    // Reduced logging to avoid console spam with detailed message contents
                    if (data.type !== 'ping' && data.type !== 'pong') {
                        // // console.log('Received WebSocket message type:', data.type);
                    }

                    switch (data.type) {
                        case 'authenticated':
                            // // console.log('Authentication successful');
                            break;
                        case 'authentication_failed':
                            console.error('Authentication failed:', data.message);
                            break;
                        case 'clear_audio_queue':
                            // // console.log('Clearing audio queue due to user interruption');
                            audioBufferQueue = [];
                            isPlaying = false;
                            break;
                        case 'audio_response':
                            if (data.audio) {
                                decodeAndPlayAudio(data.audio);
                            } else {
                                console.warn('Received empty audio_response');
                            }
                            break;
                        case 'error':
                            console.error('Server error:', data.message);
                            break;
                        case 'transcription':
                            // // console.log('Transcription received:', data.text);
                            setTranscriptionText(data.text);
                            break;
                        case 'conversation_stopped':
                            // // console.log('Conversation stopped:', data.message);
                            setIsListening(false);
                            break;
                        case 'response.canceled':
                            // // console.log('AI response canceled by server');
                            audioBufferQueue = [];
                            isPlaying = false;
                            break;
                        case 'pong':
                            // Silent handling of pong responses
                            break;
                        default:
                        // // console.log('Unknown message type:', data.type);
                    }
                } catch (error) {
                    console.error('Error processing WebSocket message:', error);
                    // Try to handle binary data if it's not JSON
                    if (typeof event.data === 'string' && event.data.startsWith('data:audio')) {
                        // // console.log('Received raw audio data');
                    }
                }
            };

            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
                alert('WebSocket connection error. Please try again.');
                setIsListening(false);
                setIsMicModalOpen(false);
            };

            ws.onclose = (event) => {
                // // console.log('WebSocket connection closed. Code:', event.code, 'Reason:', event.reason);

                // Clear ping interval
                if (pingIntervalRef.current) {
                    clearInterval(pingIntervalRef.current);
                    pingIntervalRef.current = null;
                }

                // Clean up resources if connection was closed unexpectedly
                if (isListening) {
                    setIsListening(false);
                    setIsMicModalOpen(false);
                    if (audioStreamRef.current) {
                        audioStreamRef.current.getTracks().forEach(track => track.stop());
                        audioStreamRef.current = null;
                    }
                    if (audioContextRef.current) {
                        audioContextRef.current.close();
                        audioContextRef.current = null;
                    }
                }
            };

            // Store WebSocket reference
            webSocketRef.current = ws;

        } catch (error) {
            console.error('Error initializing audio:', error);
            setIsListening(false);
            setIsMicModalOpen(false);
            alert('Unable to access microphone. Please ensure you have granted permission and try again.');

            // Clean up any resources that might have been created
            if (audioStreamRef.current) {
                audioStreamRef.current.getTracks().forEach(track => track.stop());
                audioStreamRef.current = null;
            }

            if (audioContextRef.current) {
                audioContextRef.current.close();
                audioContextRef.current = null;
            }

            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
                pingIntervalRef.current = null;
            }
        }
    };

    // Update the handleStopCommand function
    const handleStopCommand = async () => {
        try {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                setIsLoading(false);
                setIsStreaming(false);
            }
            if (currentConversationId && isAuthenticated) {
                const authToken = localStorage.getItem('authToken');
                const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.CHAT}`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        conversation_id: currentConversationId,
                        cancel: true, // Flag to indicate cancellation
                    }),
                });

                if (!response.ok) {
                    console.error('Failed to send cancellation request:', response.statusText);
                } else {
                    // // console.log('Cancellation request sent successfully for conversation:', currentConversationId);
                }
            } else {
                console.warn('No conversation ID or not authenticated, skipping cancellation');
            }

            // Clear ping interval (if used elsewhere, e.g., for audio)
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
                pingIntervalRef.current = null;
                // // console.log('Cleared ping interval');
            }
        } catch (err) {
            console.error('Error handling stop command:', err);
        }
    };

    // Update the handleCloseMicModal function
    const handleCloseMicModal = () => {
        setIsMicModalOpen(false);

        // If we're still listening when the modal is closed, stop everything
        if (isListening) {
            // // console.log('Cleaning up audio resources due to modal close');

            // Stop the media recorder if it exists
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
                mediaRecorderRef.current = null;
            }

            // Stop all audio tracks
            if (audioStreamRef.current) {
                audioStreamRef.current.getTracks().forEach(track => track.stop());
                audioStreamRef.current = null;
            }

            // Close WebSocket connection if open
            if (webSocketRef.current) {
                try {
                    if (webSocketRef.current.readyState === WebSocket.OPEN) {
                        // Try to send a closing message
                        webSocketRef.current.send(JSON.stringify({ type: 'client_disconnect' }));
                        webSocketRef.current.close();
                    }
                } catch (err) {
                    console.error('Error while closing WebSocket:', err);
                }
                webSocketRef.current = null;
            }

            // Close AudioContext if it exists
            if (audioContextRef.current) {
                try {
                    audioContextRef.current.close();
                } catch (err) {
                    console.error('Error while closing AudioContext:', err);
                }
                audioContextRef.current = null;
            }

            // Clear ping interval
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
                pingIntervalRef.current = null;
            }

            setIsListening(false);
            setTranscriptionText('');
        }
    };

    const TypingLoader = () => (
        <div className="typing-dots-container-custom">
            <div className="typing-dot typing-dot-1"></div>
            <div className="typing-dot typing-dot-2"></div>
            <div className="typing-dot typing-dot-3"></div>
        </div>
    );

    useEffect(() => {
        // Cleanup function for audio resources
        return () => {
            // // console.log('Component unmounting, cleaning up audio resources');

            // Stop any active media recorder
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                try {
                    mediaRecorderRef.current.stop();
                } catch (err) {
                    console.error('Error stopping media recorder:', err);
                }
                mediaRecorderRef.current = null;
            }

            // Close audio tracks
            if (audioStreamRef.current) {
                try {
                    audioStreamRef.current.getTracks().forEach(track => track.stop());
                } catch (err) {
                    console.error('Error stopping audio tracks:', err);
                }
                audioStreamRef.current = null;
            }

            // Close WebSocket connection
            if (webSocketRef.current) {
                try {
                    if (webSocketRef.current.readyState === WebSocket.OPEN) {
                        webSocketRef.current.send(JSON.stringify({ type: 'client_disconnect' }));
                        webSocketRef.current.close();
                    }
                } catch (err) {
                    console.error('Error closing WebSocket:', err);
                }
                webSocketRef.current = null;
            }

            // Close AudioContext
            if (audioContextRef.current) {
                try {
                    audioContextRef.current.close();
                } catch (err) {
                    console.error('Error closing AudioContext:', err);
                }
                audioContextRef.current = null;
            }

            // Clear any intervals
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
                pingIntervalRef.current = null;
            }
        };
    }, []);

    // Handler for closing chat limit popup
    const handleCloseChatLimitPopup = () => {
        setShowChatLimitPopup(false);

        try {
            if (window.mermaid) {
                try {
                    window.mermaid.clear();
                } catch (mermaidError) {
                    console.warn('Could not clear Mermaid cache:', mermaidError);
                }
            }

            const mermaidKeys = ['mermaid-diagram-cache', 'mermaid-flowchart-config', 'mermaid-sequence-config'];
            mermaidKeys.forEach(key => {
                try {
                    localStorage.removeItem(key);
                } catch (e) {
                    console.warn(`Failed to remove ${key} from localStorage`, e);
                }
            });

            const keysToPreserve = ['theme', 'language', 'fontSize'];
            const preservedData = {};

            keysToPreserve.forEach(key => {
                try {
                    const value = localStorage.getItem(key);
                    if (value) preservedData[key] = value;
                } catch (e) {
                    console.warn(`Failed to save ${key} from localStorage`, e);
                }
            });

            const chatCountValue = localStorage.getItem('geoHubFreeChatCount');
            localStorage.clear();

            if (chatCountValue) localStorage.setItem('geoHubFreeChatCount', chatCountValue);

            Object.keys(preservedData).forEach(key => {
                localStorage.setItem(key, preservedData[key]);
            });

            sessionStorage.clear();
            sessionStorage.setItem('fromChatLimit', 'true');
        } catch (error) {
            console.error('Error during cleanup before redirect:', error);
        }

        window.location.href = '/Login';
    };

    useEffect(() => {
        if (isToolsMode) {
            setDropdownSelection("Tools");
        } else if (isGraphingMode) {
            setDropdownSelection("Graphing Bot");
        } else if (isSupportMode) {
            setDropdownSelection("Support");
        } else {
            setDropdownSelection("");
        }
    }, [isToolsMode, isGraphingMode, isSupportMode]);

    const cleanupAudioResources = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        if (audioStreamRef.current) {
            audioStreamRef.current.getTracks().forEach(track => track.stop());
            audioStreamRef.current = null;
        }
        if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
            webSocketRef.current.close();
            webSocketRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
    };

    const handleDisclaimerUnderstand = () => setShowDisclaimerPopup(false);
    const handleDisclaimerClose = () => setShowDisclaimerPopup(false);

    const getCurrentBotType = () => {
        if (isSupportMode) return 'support';
        if (isGraphingMode) return 'graphing';
        return 'knowledge';
    };

    const handleOpenPromptGuide = () => setIsPromptGuideOpen(true);
    const handleClosePromptGuide = () => setIsPromptGuideOpen(false);

    const handleStartConversation = async (promptText) => {
        // Always use the local implementation for direct conversation start
        // This ensures no text is pasted into the textarea

        // Close the prompt modal immediately when a prompt is clicked
        handleClosePromptGuide();

        if (!promptText.trim() || isLoading || isUploading) {
            return;
        }

        if (!isAuthenticated && chatCount >= CHAT_LIMITS.MAX_FREE_CHATS) {
            setShowChatLimitPopup(true);
            return;
        }

        setIsLoading(true);
        abortControllerRef.current = new AbortController();
        const isNewConversation = currentConversation.length === 0;
        const userMessageId = `user-${Date.now()}`;
        const assistantMessageId = `assistant-${Date.now()}`;

        try {
            if (!isAuthenticated) {
                const newChatCount = chatCount + 1;
                setChatCount(newChatCount);
                localStorage.setItem(LOCAL_STORAGE_KEYS.FREE_CHAT_COUNT, newChatCount.toString());
                if (newChatCount === CHAT_LIMITS.MAX_FREE_CHATS) {
                    setTimeout(() => setShowChatLimitPopup(true), 1000);
                }
            }

            let conversationIdToUse = currentConversationId;
            if (isAuthenticated && !conversationIdToUse) {
                const newConversationId = await handleCreateNewConversation();
                if (!newConversationId) throw new Error('Failed to create conversation. Please try again.');
                conversationIdToUse = newConversationId;
                // console.log('🆔 Set conversationIdToUse to:', conversationIdToUse);
            }

            const trimmedInput = promptText.trim();

            const userMessage = {
                id: userMessageId,
                role: 'user',
                content: trimmedInput,
                created_at: new Date().toISOString(),
            };

            setCurrentConversation(prev => [...prev, userMessage]);
            setShowPrompt(false);
            setUploadedFile(null);

            handleNewMessage({
                id: assistantMessageId,
                role: 'assistant',
                content: '',
                created_at: new Date().toISOString(),
            });

            let endpoint;
            let requestBody;
            let headers = { 'Content-Type': 'application/json' };

            if (isAuthenticated && token) headers['Authorization'] = `Bearer ${token}`;

            const assistant = localStorage.getItem('assistantInfo');
            const assistantInfo = assistant ? JSON.parse(assistant) : null;

            if (isAuthenticated) {
                endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.CHAT}`;
                requestBody = JSON.stringify({
                    query: trimmedInput,
                    conversation_id: conversationIdToUse,
                    assistant_id: assistantInfo?.assistantId || '',
                    file_id: assistantInfo?.fileId || ''
                });
                headers = {
                    'Content-Type': 'application/json',
                    ...(isAuthenticated && token && { 'Authorization': `Bearer ${token}` })
                };
            } else {
                endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.CUSTOM_CHAT}`;
                requestBody = JSON.stringify({
                    query: trimmedInput
                });
                headers = {
                    'Content-Type': 'application/json'
                };
            }

            // Log the request details (excluding AI response content)
            const requestTimestamp = new Date().toISOString();
            // console.log('🚀 REQUEST:', {
            //     timestamp: requestTimestamp,
            //     endpoint: endpoint,
            //     method: 'POST',
            //     requestType: 'JSON',
            //     conversationId: conversationIdToUse,
            //     isAuthenticated: isAuthenticated,
            //     userId: userId,
            //     isNewConversation: isNewConversation,
            //     userMessageId: userMessageId,
            //     assistantMessageId: assistantMessageId,
            //     userQuery: trimmedInput
            // });

            let response;
            if (isAuthenticated && token) {
                // Use the authenticated API client for authenticated users
                const { apiClient } = await import('./utils/apiClient');
                response = await apiClient.request(endpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: requestBody,
                    signal: abortControllerRef.current.signal
                });
            } else {
                // Use regular fetch for unauthenticated users
                response = await fetch(endpoint, {
                    method: 'POST',
                    headers: headers,
                    body: requestBody,
                    signal: abortControllerRef.current.signal
                });
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail?.[0]?.msg || errorData.error || 'Failed to process request');
            }

            // Handle streaming text response
            // console.log('📡 About to call handleStreamingResponse with conversationIdToUse:', {
            //     conversationIdToUse,
            //     type: typeof conversationIdToUse,
            //     isNull: conversationIdToUse === null,
            //     isUndefined: conversationIdToUse === undefined
            // });
            await handleStreamingResponse(response, assistantMessageId, conversationIdToUse);

            // Close the sidebar when a prompt is clicked (if handleToggleSidebar is provided)
            // We'll dispatch a custom event to close the sidebar
            window.dispatchEvent(new CustomEvent('closeSidebar'));

        } catch (error) {
            if (error.name === 'AbortError') {
                // console.log('Fetch aborted by user.');
            } else {
                console.error("Error:", error);
                handleNewMessage({
                    id: assistantMessageId,
                    role: 'assistant',
                    content: error.message || 'Failed to process request',
                    created_at: new Date().toISOString(),
                });
            }
            // Ensure prompt modal is closed even if there's an error
            handleClosePromptGuide();
        } finally {
            setIsLoading(false);
            setIsStreaming(false);
            abortControllerRef.current = null;
        }
    };

    useEffect(() => {
        if (currentConversation.length === 0) {
            setShowPrompt(true);
            responseCountRef.current = 0;
            hasFetchedHistoryRef.current = false;
        }
    }, [currentConversation]);

    const FunctionCallStatus = ({ functionCall, searchStatus }) => {
        const [displayText, setDisplayText] = useState('');
        const [isComplete, setIsComplete] = useState(false);
        const textRef = useRef('');

        useEffect(() => {
            let statusText = '';

            if (searchStatus && searchStatus.state === 'searching') {
                statusText = `Searching knowledge base${searchStatus.query ? ` for "${searchStatus.query}"` : ''}...`;
            } else if (searchStatus && searchStatus.state === 'completed') {
                statusText = `Search completed${searchStatus.query ? ` for "${searchStatus.query}"` : ''}`;
            } else if (functionCall) {
                if (functionCall.state === 'started') {
                    statusText = 'Processing your request...';
                } else if (functionCall.state === 'arguments') {
                    statusText = 'Analyzing information...';
                } else if (functionCall.state === 'executing') {
                    statusText = 'Finding information...';
                } else if (functionCall.state === 'completed') {
                    statusText = 'Processing complete';
                }
            }

            if (!statusText) return;

            textRef.current = statusText;
            setDisplayText('');
            setIsComplete(false);

            let charIndex = 0;
            const streamingSpeed = 15;

            const streamInterval = setInterval(() => {
                if (charIndex <= textRef.current.length) {
                    setDisplayText(textRef.current.substring(0, charIndex));
                    charIndex++;
                } else {
                    clearInterval(streamInterval);
                    setIsComplete(true);
                }
            }, streamingSpeed);

            return () => clearInterval(streamInterval);
        }, [functionCall, searchStatus]);

        if (!functionCall && !searchStatus) return null;

        return (
            <div className="assistant-text-container">
                <em>{displayText}</em>
                {!isComplete && <span className="typing-cursor">|</span>}
            </div>
        );
    };

    const startEditing = (message) => {
        setEditingMessageId(message.id);
        if (typeof message.content === 'string') {
            setEditValue(message.content);
        } else if (typeof message.content === 'object' && message.content.text) {
            setEditValue(message.content.text);
        }
    };

    const cancelEditing = () => {
        setEditingMessageId(null);
        setEditValue('');
    };

    const submitEdit = async (messageId) => {
        if (!editValue.trim()) {
            cancelEditing();
            return;
        }

        const editedMessage = currentConversation.find(msg => msg.id === messageId);
        if (!editedMessage) {
            cancelEditing();
            return;
        }

        // Update the message content in UI
        const messageIndex = currentConversation.findIndex(msg => msg.id === messageId);
        if (messageIndex === -1) {
            cancelEditing();
            return;
        }

        // Update message content while preserving its structure
        let updatedMessage;
        if (typeof editedMessage.content === 'string') {
            updatedMessage = { ...editedMessage, content: editValue };
        } else if (typeof editedMessage.content === 'object' && editedMessage.content.text) {
            updatedMessage = {
                ...editedMessage,
                content: { ...editedMessage.content, text: editValue }
            };
        } else {
            updatedMessage = { ...editedMessage, content: editValue };
        }

        // Create a new conversation array with the edited message
        const updatedConversation = [...currentConversation];
        updatedConversation[messageIndex] = updatedMessage;

        // Remove AI responses that follow this message
        if (messageIndex < updatedConversation.length - 1 && updatedConversation[messageIndex + 1].role === 'assistant') {
            updatedConversation.splice(messageIndex + 1);
        }

        // Update the conversation
        setCurrentConversation(updatedConversation);

        // Clean up edit state
        cancelEditing();

        // Resend the edited message as a new request
        await sendEditedMessage(updatedMessage);
    };

    const sendEditedMessage = async (editedMessage) => {
        if (!editedMessage || isLoading || isUploading) return;

        setIsLoading(true);
        abortControllerRef.current = new AbortController();

        const assistantMessageId = `assistant-${Date.now()}`;

        try {
            // Add initial assistant message showing typing indicator
            handleNewMessage({
                id: assistantMessageId,
                role: 'assistant',
                content: '',
                created_at: new Date().toISOString(),
            });

            // Prepare request based on mode and content type
            let endpoint;
            let requestBody;
            let headers = {
                'Content-Type': 'application/json',
            };

            // Add authorization header only if authenticated
            if (isAuthenticated && token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // Extract the message content depending on the structure
            let messageContent;
            if (typeof editedMessage.content === 'string') {
                messageContent = editedMessage.content;
            } else if (typeof editedMessage.content === 'object' && editedMessage.content.text) {
                messageContent = editedMessage.content.text;
            } else {
                messageContent = JSON.stringify(editedMessage.content);
            }

            if (isGraphingMode) {
                // Handle graphing mode
                endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.GRAPH}`;

                if (isChartAnalysisMode && currentChartData && currentPlotConfig) {
                    // Chart analysis mode
                    requestBody = {
                        mode: 'analyze',
                        query: messageContent,
                        chartData: currentChartData,
                        plotConfig: currentPlotConfig
                    };
                } else {
                    // Chart generation mode
                    requestBody = {
                        mode: 'generate',
                        csvData: uploadedFile?.content,
                        chartType: chartType
                    };
                }
            } else if (typeof editedMessage.content === 'object' && editedMessage.content.image) {
                // Handle image content
                endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.IMAGE}`;
                requestBody = {
                    query: messageContent,
                    image_url: editedMessage.content.image,
                    conversation_id: isAuthenticated ? currentConversationId : null
                };
            } else {
                // Handle text content
                if (isAuthenticated) {
                    endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.CHAT}`;
                    requestBody = {
                        query: messageContent,
                        conversation_id: currentConversationId
                    };
                } else {
                    endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.CUSTOM_CHAT}`;

                    requestBody = {
                        query: messageContent
                    };
                }
            }

            // Log the edited message request
            // console.log('✏️ EDITED REQUEST:', {
            //     timestamp: new Date().toISOString(),
            //     endpoint: endpoint,
            //     method: 'POST',
            //     requestType: 'JSON',
            //     conversationId: currentConversationId,
            //     isAuthenticated: isAuthenticated,
            //     userId: userId,
            //     assistantMessageId: assistantMessageId,
            //     messageType: typeof editedMessage.content === 'string' ? 'text' : 'object',
            //     userQuery: messageContent
            // });

            // Send the request
            let response;
            if (isAuthenticated && token) {
                // Use the authenticated API client for authenticated users
                const { apiClient } = await import('./utils/apiClient');
                response = await apiClient.request(endpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(requestBody),
                    signal: abortControllerRef.current.signal
                });
            } else {
                // Use regular fetch for unauthenticated users
                response = await fetch(endpoint, {
                    method: 'POST',
                    headers: headers,
                    body: JSON.stringify(requestBody),
                    signal: abortControllerRef.current.signal
                });
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail?.[0]?.msg || errorData.error || 'Failed to process request');
            }

            // Handle the response based on the mode
            if (isGraphingMode && !isChartAnalysisMode) {
                // Handle Plotly.js graph response (generation mode)
                await handleGraphingResponse(response, assistantMessageId);
            } else {
                // Handle streaming text response
                await handleStreamingResponse(response, assistantMessageId, currentConversationId);
            }

        } catch (error) {
            console.error('Error sending edited message:', error);
            handleError(error, assistantMessageId);
        } finally {
            setIsLoading(false);
        }
    };

    // Process streaming text response
    const handleStreamingResponse = async (response, assistantMessageId, conversationId = null) => {
        // console.log('🔄 handleStreamingResponse called with conversationId:', conversationId);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulatedContent = '';
        let images = [];
        let functionCallData = null;
        let searchStatusData = null;
        setTempFunctionMessages([]);

        try {
            while (true) {
                const { value, done } = await reader.read();
                if (done) {
                    // // console.log('Stream completed, calling handleStreamCompletion');

                    // If we have content, remove all temporary function messages
                    if (accumulatedContent.trim()) {
                        setTempFunctionMessages([]);
                    }

                    // Dispatch completion event with conversation ID
                    handleStreamCompletion(conversationId || currentConversationId);
                    break;
                }

                const chunk = decoder.decode(value);
                const lines = chunk.split('\n\n');

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));
                            // // console.log('Received data type:', data.type, data);

                            // Handle different response types
                            switch (data.type) {
                                case 'chunk':
                                    // Filter out unwanted download text
                                    let filteredContent = data.content;
                                    if (filteredContent.includes('Download the dummy CSV file')) {
                                        filteredContent = filteredContent.replace(/Download the dummy CSV file/g, '').trim();
                                    }

                                    accumulatedContent += filteredContent;

                                    // If this is the first chunk with actual content, remove temporary messages
                                    if (accumulatedContent.trim() && tempFunctionMessages.length > 0) {
                                        setTempFunctionMessages([]);
                                    }

                                    // Update the message with current data
                                    handleNewMessage({
                                        id: assistantMessageId,
                                        role: 'assistant',
                                        content: accumulatedContent,
                                        images: images,
                                        created_at: new Date().toISOString(),
                                        functionCall: functionCallData,
                                        searchStatus: searchStatusData
                                    });
                                    break;

                                case 'images':
                                    // Store images for later rendering
                                    console.log('🖼️ Frontend: Received images:', data);
                                    
                                    if (data.images && Array.isArray(data.images)) {
                                        // Process and normalize incoming images
                                        const newImages = data.images.map((img, idx) => {
                                            const normalizedImg = {
                                                url: img.url || img.base64 || img.src,
                                                name: img.name || img.description || `Image ${idx + 1}`,
                                                id: img.id || `img-${Date.now()}-${idx}`,
                                                isGraph: false
                                            };
                                            return normalizedImg;
                                        });
                                        
                                        // Merge with existing images (avoid duplicates)
                                        const existingUrls = new Set(images.map(img => img.url));
                                        const uniqueNewImages = newImages.filter(img => !existingUrls.has(img.url));
                                        images = [...images, ...uniqueNewImages];
                                        
                                        console.log(`🖼️ Frontend: Total images: ${images.length}`);
                                    }

                                    // Update the message with current data
                                    handleNewMessage({
                                        id: assistantMessageId,
                                        role: 'assistant',
                                        content: accumulatedContent,
                                        images: images,
                                        created_at: new Date().toISOString(),
                                        functionCall: functionCallData,
                                        searchStatus: searchStatusData
                                    });
                                    break;

                                case 'csv_images':
                                case 'pdf_images':
                                    // Handle CSV/PDF-generated images
                                    console.log('🖼️ Frontend: Received file images chunk:', data.type);
                                    console.log('🖼️ Frontend: Raw data:', data);
                                    
                                    if (data.images && Array.isArray(data.images)) {
                                        // Process and normalize incoming images
                                        const newImages = data.images.map((img, idx) => {
                                            const normalizedImg = {
                                                url: img.url || img.base64 || img.src,
                                                name: img.name || img.description || `Generated visualization ${idx + 1}`,
                                                id: img.id || `img-${Date.now()}-${idx}`,
                                                isGraph: true
                                            };
                                            console.log(`🖼️ Frontend: Normalized image ${idx}:`, {
                                                hasUrl: !!normalizedImg.url,
                                                urlLength: normalizedImg.url?.length,
                                                urlPrefix: normalizedImg.url?.substring(0, 50),
                                                name: normalizedImg.name
                                            });
                                            return normalizedImg;
                                        });
                                        
                                        // Merge with existing images (avoid duplicates)
                                        const existingUrls = new Set(images.map(img => img.url));
                                        const uniqueNewImages = newImages.filter(img => !existingUrls.has(img.url));
                                        images = [...images, ...uniqueNewImages];
                                        
                                        console.log(`🖼️ Frontend: Total images after merge: ${images.length}`);
                                        console.log(`🖼️ Frontend: Images array:`, images.map(img => ({
                                            url: img.url?.substring(0, 50) + '...',
                                            name: img.name
                                        })));
                                    } else {
                                        console.error('🖼️ Frontend: Invalid images data received:', data);
                                    }

                                    // Update the message with images
                                    handleNewMessage({
                                        id: assistantMessageId,
                                        role: 'assistant',
                                        content: accumulatedContent,
                                        images: images,
                                        created_at: new Date().toISOString(),
                                        functionCall: functionCallData,
                                        searchStatus: searchStatusData
                                    });
                                    console.log(`🖼️ Frontend: Updated message with ${images.length} images`);
                                    break;

                                case 'function_call_started':
                                    // Create a temporary function message
                                    const funcStartId = `function-${Date.now()}-${data.functionName}`;
                                    setTempFunctionMessages(prev => [
                                        ...prev,
                                        {
                                            id: funcStartId,
                                            role: 'function',
                                            functionName: data.functionName,
                                            state: 'started',
                                            content: `Processing your request...`,
                                            created_at: new Date().toISOString(),
                                        }
                                    ]);

                                    // Also update functionCallData for the main message display
                                    functionCallData = {
                                        state: 'started',
                                        name: data.functionName,
                                        arguments: ''
                                    };

                                    // Update the message with function call info
                                    handleNewMessage({
                                        id: assistantMessageId,
                                        role: 'assistant',
                                        content: accumulatedContent,
                                        images: images,
                                        functionCall: functionCallData,
                                        searchStatus: searchStatusData,
                                        created_at: new Date().toISOString()
                                    });
                                    break;

                                case 'function_call_arguments':
                                    // Update function message with arguments
                                    setTempFunctionMessages(prev =>
                                        prev.map(msg =>
                                            msg.role === 'function' &&
                                                msg.functionName === data.functionName &&
                                                msg.state === 'started'
                                                ? {
                                                    ...msg,
                                                    state: 'arguments',
                                                    content: `Analyzing your request...`
                                                }
                                                : msg
                                        )
                                    );

                                    // Update functionCallData with arguments
                                    if (functionCallData) {
                                        functionCallData = {
                                            ...functionCallData,
                                            state: 'arguments',
                                            arguments: (functionCallData.arguments || '') + (data.functionArguments || '')
                                        };
                                    }

                                    // Update the message with function call info
                                    handleNewMessage({
                                        id: assistantMessageId,
                                        role: 'assistant',
                                        content: accumulatedContent,
                                        images: images,
                                        functionCall: functionCallData,
                                        searchStatus: searchStatusData,
                                        created_at: new Date().toISOString()
                                    });
                                    break;

                                case 'function_execution_started':
                                    // Update function message to executing state
                                    setTempFunctionMessages(prev =>
                                        prev.map(msg =>
                                            msg.role === 'function' &&
                                                msg.functionName === data.functionName
                                                ? {
                                                    ...msg,
                                                    state: 'executing',
                                                    content: `Working on your request...`
                                                }
                                                : msg
                                        )
                                    );

                                    // Update functionCallData to executing state
                                    if (functionCallData) {
                                        functionCallData = {
                                            ...functionCallData,
                                            state: 'executing'
                                        };
                                    }

                                    // Update the message with function call info
                                    handleNewMessage({
                                        id: assistantMessageId,
                                        role: 'assistant',
                                        content: accumulatedContent,
                                        images: images,
                                        functionCall: functionCallData,
                                        searchStatus: searchStatusData,
                                        created_at: new Date().toISOString()
                                    });
                                    break;

                                case 'function_execution_completed':
                                    // Update function message to completed state
                                    setTempFunctionMessages(prev =>
                                        prev.map(msg =>
                                            msg.role === 'function' &&
                                                msg.functionName === data.functionName
                                                ? {
                                                    ...msg,
                                                    state: 'completed',
                                                    content: `Request processed successfully`
                                                }
                                                : msg
                                        )
                                    );

                                    // Update functionCallData to completed state
                                    if (functionCallData) {
                                        functionCallData = {
                                            ...functionCallData,
                                            state: 'completed'
                                        };
                                    }

                                    // Update the message with function call info
                                    handleNewMessage({
                                        id: assistantMessageId,
                                        role: 'assistant',
                                        content: accumulatedContent,
                                        images: images,
                                        functionCall: functionCallData,
                                        searchStatus: searchStatusData,
                                        created_at: new Date().toISOString()
                                    });
                                    break;

                                case 'knowledge_base_search_started':
                                    // Create search message
                                    const searchId = `search-${Date.now()}`;
                                    setTempFunctionMessages(prev => [
                                        ...prev,
                                        {
                                            id: searchId,
                                            role: 'function',
                                            type: 'search',
                                            state: 'searching',
                                            content: `Searching knowledge base...`,
                                            created_at: new Date().toISOString(),
                                        }
                                    ]);

                                    // Update searchStatusData
                                    searchStatusData = {
                                        state: 'searching',
                                        query: data.content
                                    };

                                    // Update the message with search status
                                    handleNewMessage({
                                        id: assistantMessageId,
                                        role: 'assistant',
                                        content: accumulatedContent,
                                        images: images,
                                        functionCall: functionCallData,
                                        searchStatus: searchStatusData,
                                        created_at: new Date().toISOString()
                                    });
                                    break;

                                case 'knowledge_base_search_completed':
                                    // Update search message
                                    setTempFunctionMessages(prev =>
                                        prev.map(msg =>
                                            msg.role === 'function' &&
                                                msg.type === 'search' &&
                                                msg.state === 'searching'
                                                ? {
                                                    ...msg,
                                                    state: 'completed',
                                                    content: `Search completed`
                                                }
                                                : msg
                                        )
                                    );

                                    // Update searchStatusData to completed
                                    searchStatusData = {
                                        state: 'completed',
                                        query: searchStatusData?.query
                                    };

                                    // Update the message with search status
                                    handleNewMessage({
                                        id: assistantMessageId,
                                        role: 'assistant',
                                        content: accumulatedContent,
                                        images: images,
                                        functionCall: functionCallData,
                                        searchStatus: searchStatusData,
                                        created_at: new Date().toISOString()
                                    });
                                    break;

                                case 'error':
                                    throw new Error(data.error || 'Error processing request');

                                case 'csv_analysis_started':
                                    // Handle CSV analysis start notification
                                    // console.log('CSV analysis started:', data.content);
                                    break;

                                case 'csv_analysis_progress':
                                    // Handle CSV analysis progress updates
                                    // console.log('CSV analysis progress:', data.content);
                                    // You could show a progress indicator here if needed
                                    break;

                                case 'csv_images':
                                case 'pdf_images':
                                    // Handle CSV/PDF-generated images (handler 2)
                                    console.log('🖼️ Frontend: Received file images chunk (handler 2):', data.type);
                                    console.log('🖼️ Frontend: Raw data (handler 2):', data);
                                    
                                    if (data.images && Array.isArray(data.images)) {
                                        // Process and normalize incoming images
                                        const newImages = data.images.map((img, idx) => {
                                            const normalizedImg = {
                                                url: img.url || img.base64 || img.src,
                                                name: img.name || img.description || `Generated visualization ${idx + 1}`,
                                                id: img.id || `img-${Date.now()}-${idx}`,
                                                isGraph: true
                                            };
                                            console.log(`🖼️ Frontend: Normalized image ${idx} (handler 2):`, {
                                                hasUrl: !!normalizedImg.url,
                                                urlLength: normalizedImg.url?.length,
                                                urlPrefix: normalizedImg.url?.substring(0, 50),
                                                name: normalizedImg.name
                                            });
                                            return normalizedImg;
                                        });
                                        
                                        // Merge with existing images (avoid duplicates)
                                        const existingUrls = new Set(images.map(img => img.url));
                                        const uniqueNewImages = newImages.filter(img => !existingUrls.has(img.url));
                                        images = [...images, ...uniqueNewImages];
                                        
                                        console.log(`🖼️ Frontend: Total images after merge (handler 2): ${images.length}`);
                                        console.log(`🖼️ Frontend: Images array (handler 2):`, images.map(img => ({
                                            url: img.url?.substring(0, 50) + '...',
                                            name: img.name
                                        })));
                                    } else {
                                        console.error('🖼️ Frontend: Invalid images data received (handler 2):', data);
                                    }

                                    // Update the message with images
                                    handleNewMessage({
                                        id: assistantMessageId,
                                        role: 'assistant',
                                        content: accumulatedContent,
                                        images: images,
                                        created_at: new Date().toISOString(),
                                        functionCall: functionCallData,
                                        searchStatus: searchStatusData
                                    });
                                    console.log(`🖼️ Frontend: Updated message with ${images.length} images (handler 2)`);
                                    break;

                                case 'assistant_info':
                                    if (data.assistantId && data.fileId) {
                                        const now = new Date();
                                        const item = {
                                            assistantId: data.assistantId,
                                            fileId: data.fileId,
                                            downloadedFileIds: data.downloadedFileIds || [],
                                            expiry: now.getTime() + 60 * 60 * 1000, // 1 hour in milliseconds
                                        };
                                        localStorage.setItem('assistantInfo', JSON.stringify(item));
                                        // console.log('💾 Saved assistant info to localStorage:', item);

                                        // Also save downloaded file IDs separately for easy access
                                        if (data.downloadedFileIds && data.downloadedFileIds.length > 0) {
                                            const downloadedFileItem = {
                                                fileIds: data.downloadedFileIds,
                                                timestamp: now.toISOString(),
                                                expiry: now.getTime() + 10 * 60 * 1000
                                            };
                                            localStorage.setItem('downloadedFileIds', JSON.stringify(downloadedFileItem));
                                            // console.log('📁 Saved downloaded file IDs to localStorage:', downloadedFileItem);
                                        }
                                    }
                                    break;

                                case 'finish':
                                    // Handle finish - clear temporary function messages and function call status
                                    // console.log('✅ Received finish signal in streaming response, clearing temporary messages');
                                    setTempFunctionMessages([]);
                                    // Clear function call status from the assistant message
                                    handleNewMessage({
                                        id: assistantMessageId,
                                        role: 'assistant',
                                        content: accumulatedContent || '',
                                        images: images || [],
                                        created_at: new Date().toISOString(),
                                        functionCall: null,
                                        searchStatus: null
                                    });
                                    // Don't return here - continue processing in case images come after finish
                                    break;

                                case 'end':
                                    // Handle end - this is the final signal
                                    // console.log('🔄 STREAM COMPLETED - About to call handleStreamCompletion with:', {
                                    //     conversationId,
                                    //     currentConversationId,
                                    //     finalId: conversationId || currentConversationId
                                    // });

                                    // Ensure we have a valid conversation ID
                                    const finalConversationId = conversationId || currentConversationId;
                                    if (finalConversationId) {
                                        handleStreamCompletion(finalConversationId);
                                    } else {
                                        console.error('❌ No valid conversation ID available for handleStreamCompletion');
                                    }

                                    return; // Exit cleanly

                                default:
                                    // // console.log('Unhandled data type:', data.type);
                                    break;
                            }
                        } catch (e) {
                            console.error('Error processing chunk:', e);
                            continue;
                        }
                    }
                }
            }
        } catch (error) {
            if (error.name === 'AbortError' || error.message?.includes('aborted') || error.message?.includes('cancelled by user')) {
                // console.log('Stream was cancelled by user');
                // Clear any temporary function messages when cancelled
                setTempFunctionMessages([]);
                // Clear function call status from the current assistant message
                handleNewMessage({
                    id: assistantMessageId,
                    role: 'assistant',
                    content: accumulatedContent || '',
                    images: images || [],
                    created_at: new Date().toISOString(),
                    functionCall: null,
                    searchStatus: null
                });
                // Clean exit - don't show error to user
                return;
            } else {
                console.error('Stream reading error:', error);
                handleError(error, assistantMessageId);
            }
        }
    };

    // Function to handle graphing responses
    const handleGraphingResponse = async (response, assistantMessageId) => {
        try {
            const data = await response.json();

            // Check if we have a valid plot configuration
            if (!data.plotConfig || !data.plotConfig.data) {
                throw new Error('Invalid plot configuration received from server');
            }

            const plotConfig = data.plotConfig;

            // Store chart data and config for potential future analysis
            setCurrentChartData(data.chartData || []);
            setCurrentPlotConfig(plotConfig);

            // Enable chart analysis mode after generating a chart
            setIsChartAnalysisMode(true);

            // Convert the Plotly.js configuration to a Chart.js configuration
            const chartConfig = convertPlotlyToChartJs(plotConfig);

            // Check if there is actual data to display
            const hasData = chartConfig.data.datasets &&
                chartConfig.data.datasets.length > 0 &&
                chartConfig.data.datasets.some(ds => ds.data && ds.data.length > 0);

            if (!hasData) {
                handleNewMessage({
                    id: assistantMessageId,
                    role: 'assistant',
                    content: 'Unable to generate a meaningful chart from the provided data. Please check your CSV format and try again.',
                    created_at: new Date().toISOString(),
                });
            } else {
                // Extract metadata from the plot configuration
                const chartType = getChartTypeName(plotConfig);
                const seriesNames = getSeriesNames(plotConfig);
                const dataPoints = getDataPointCount(plotConfig);

                // Create a chart object for the response
                handleNewMessage({
                    id: assistantMessageId,
                    role: 'assistant',
                    content: {
                        type: 'chart',
                        title: plotConfig.layout?.title?.text || 'Generated Chart',
                        chartConfig,
                        chartType,
                        series: seriesNames,
                        dataPoints
                    },
                    created_at: new Date().toISOString(),
                });
            }
        } catch (error) {
            console.error('Error processing graph response:', error);
            handleError(error, assistantMessageId);
        }
    };

    // Handle errors from API calls or other operations
    const handleError = (error, messageId) => {
        console.error('Error encountered:', error);

        // Update the message with error information
        handleNewMessage({
            id: messageId,
            role: 'assistant',
            content: `An error occurred: ${error.message || 'Unknown error'}`,
            created_at: new Date().toISOString(),
            error: true
        });

        // Clean up any in-progress states
        setTempFunctionMessages([]);
        setIsLoading(false);

        // Handle abort controller to ensure we don't have lingering requests
        if (abortControllerRef.current) {
            abortControllerRef.current = null;
        }
    };

    const handleCopyResponse = async (message) => {
        const textToCopy = typeof message.content === "string"
            ? message.content
            : JSON.stringify(message.content);

        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(textToCopy);
            } else {
                // Fallback for insecure context or unsupported browsers
                const textArea = document.createElement("textarea");
                textArea.value = textToCopy;
                textArea.style.position = "fixed"; // avoid scrolling to bottom
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
            }

            setCopiedMessageId(message.id);
            setTimeout(() => setCopiedMessageId(null), 2000);
        } catch (err) {
            console.error("Failed to copy text: ", err);
        }
    };


    const renderMessageContent = (message) => {
        if (message.type === 'math' || message.content.includes('$')) {
            return <MarkdownWithMath content={message.content} />;
        }

        // Handle CSV file content with HTML table
        if (typeof message.content === 'object' && message.content.csvData) {
            const csvTable = csvToHtmlTable(message.content.csvData, message.content.fileName);

            return (
                <div>
                    {csvTable}
                    {message.content.text && (
                        <div style={{ marginTop: '16px' }}>
                            <ReactMarkdown>{message.content.text}</ReactMarkdown>
                        </div>
                    )}
                </div>
            );
        }

        // Handle regular text messages that might contain CSV data
        if (typeof message.content === 'string') {
            const csvMatches = detectAndExtractCSV(message.content);

            if (csvMatches && csvMatches.length > 0) {
                let remainingContent = message.content;
                const elements = [];
                let lastIndex = 0;

                // Sort matches by start index to process them in order
                csvMatches.sort((a, b) => a.startIndex - b.startIndex);

                csvMatches.forEach((match, index) => {
                    // Add text before the CSV match
                    if (match.startIndex > lastIndex) {
                        const textBefore = remainingContent.substring(lastIndex, match.startIndex);
                        if (textBefore.trim()) {
                            elements.push(
                                <div key={`text-${index}`} style={{ marginBottom: '16px' }}>
                                    <ReactMarkdown>{textBefore}</ReactMarkdown>
                                </div>
                            );
                        }
                    }

                    // Add the CSV table
                    const csvTable = csvToHtmlTable(match.csvData);
                    if (csvTable) {
                        elements.push(
                            <div key={`csv-${index}`}>
                                {csvTable}
                            </div>
                        );
                    }

                    lastIndex = match.endIndex;
                });

                // Add remaining text after the last CSV match
                if (lastIndex < remainingContent.length) {
                    const textAfter = remainingContent.substring(lastIndex);
                    if (textAfter.trim()) {
                        elements.push(
                            <div key="text-after" style={{ marginTop: '16px' }}>
                                <ReactMarkdown>{textAfter}</ReactMarkdown>
                            </div>
                        );
                    }
                }

                // Add download button if there are file IDs in localStorage
                const fileIds = getDownloadedFileIds();
                if (fileIds.length > 0 && message.role === 'assistant') {
                    elements.push(
                        <div key="download-button" style={{ marginTop: '10px' }}>
                            <button
                                onClick={async () => {
                                    const { downloadFileFromId } = await import('./utils/filedownload.js');
                                    for (const fileId of fileIds) {
                                        const success = await downloadFileFromId(fileId);
                                        if (success) {
                                        } else {
                                            console.error(`Failed to download file ${fileId}.`);
                                        }
                                    }
                                }}
                                style={{ padding: '5px 10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                            >
                                Download Files
                            </button>
                        </div>
                    );
                }

                return <div>{elements}</div>;
            }
        }

        // Handle other message types
        // Add download button for non-CSV content if there are file IDs
        const fileIds = getDownloadedFileIds();
        if (fileIds.length > 0 && message.role === 'assistant') {
            return (
                <div>
                    <ReactMarkdown>
                        {message.content}
                    </ReactMarkdown>
                    <button
                        onClick={async () => {
                            const { downloadFileFromId } = await import('./utils/filedownload.js');
                            for (const fileId of fileIds) {
                                const success = await downloadFileFromId(fileId);
                                if (!success) {
                                    console.error(`Failed to download file ${fileId}.`);
                                }
                            }
                        }}
                        style={{ marginTop: '10px', padding: '5px 10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                    >
                        Download Files
                    </button>
                </div>
            );
        }

        return (
            <ReactMarkdown>
                {message.content}
            </ReactMarkdown>
        );
    };

    // Add new useEffect for handling page refresh
    useEffect(() => {
        const loadConversationFromUrl = async () => {
            const path = window.location.pathname;
            const match = path.match(/\/c\/([^\/]+)/);

            if (match && match[1]) {
                const conversationId = match[1];
                setCurrentConversationId(conversationId);

                try {
                    // Import the API client dynamically to avoid circular dependency
                    const { apiClient } = await import('./utils/apiClient');

                    const response = await apiClient.post(`${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.GET_CHAT}`, {
                        conversation_id: conversationId
                    });

                    if (!response.ok) {
                        throw new Error(`Failed to fetch conversation: ${response.status}`);
                    }

                    const data = await response.json();
                    if (data.chat_history) {
                        setCurrentConversation(data.chat_history);

                        // Restore scroll position after conversation loads
                        setTimeout(() => {
                            if (messagesContainerRef.current) {
                                messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                            }
                        }, 100);
                    }
                } catch (error) {
                    console.error('Error loading conversation:', error);
                }
            }
        };

        loadConversationFromUrl();
    }, []);

    const handleOpenFeedback = () => {
        window.open('https://forms.office.com/r/gp2dd1xEA5?origin=lprLink', '_blank');
    };

    useEffect(() => {
        initializePDFJS().catch(error => {
            console.error('Failed to initialize PDF.js:', error);
        });
    }, []);

    useEffect(() => {
        const handleNavigateToConversation = (event) => {
            if (event.detail?.conversationId) {
                setCurrentConversation([]);
                setCurrentConversationId(event.detail.conversationId);

                // Reset scroll position when navigating to conversation
                setTimeout(() => {
                    if (messagesContainerRef.current) {
                        messagesContainerRef.current.scrollTop = 0;
                    }
                }, 100);
            }
        };

        const handleClearConversation = () => {
            setCurrentConversation([]);
            setCurrentConversationId(null);
            setUserInput('');
            setShowPrompt(true);

            // Reset scroll position when clearing conversation
            setTimeout(() => {
                if (messagesContainerRef.current) {
                    messagesContainerRef.current.scrollTop = 0;
                }
            }, 100);
        };

        const handlePopState = () => {
            setCurrentConversation([]);
            setCurrentConversationId(null);
            setUserInput('');
            setShowPrompt(true);
            setIsLoading(false);
            setIsUploading(false);
        };

        window.addEventListener('navigateToConversation', handleNavigateToConversation);
        window.addEventListener('clearConversation', handleClearConversation);
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('navigateToConversation', handleNavigateToConversation);
            window.removeEventListener('clearConversation', handleClearConversation);
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    useEffect(() => {
        if (currentConversationId && currentConversation.length > 0) {
            const currentPath = window.location.pathname;
            const expectedPath = `/c/${currentConversationId}`;

            if (currentPath !== expectedPath) {
                window.history.pushState({ conversationId: currentConversationId }, '', expectedPath);
            }
        }
    }, [currentConversationId, currentConversation]);

    const handleBackClick = () => {
        window.history.pushState({}, '', '/');
        setCurrentConversation([]);
        setCurrentConversationId(null);
        setShowPrompt(true);
        window.dispatchEvent(new CustomEvent('clearConversation'));
    };

    // Utility function to get downloaded file IDs from localStorage
    const getDownloadedFileIds = () => {
        try {
            const downloadedFileItem = localStorage.getItem('downloadedFileIds');
            if (downloadedFileItem) {
                const parsed = JSON.parse(downloadedFileItem);
                const now = new Date().getTime();

                // Check if the data has expired
                if (parsed.expiry && now > parsed.expiry) {
                    localStorage.removeItem('downloadedFileIds');

                    return [];
                }

                return parsed.fileIds || [];
            }
            return [];
        } catch (error) {
            console.error('Error retrieving downloaded file IDs from localStorage:', error);
            return [];
        }
    };

    // Component for download button
    const DownloadButton = () => {
        const [fileIds, setFileIds] = useState([]);

        useEffect(() => {
            const checkFileIds = () => {
                const ids = getDownloadedFileIds();
                setFileIds(ids);
            };

            checkFileIds();
            // Check every 2 seconds for changes
            const interval = setInterval(checkFileIds, 2000);

            return () => clearInterval(interval);
        }, []);

        const handleDownload = async () => {
            try {
                const { downloadFileFromId } = await import('./utils/filedownload.js');
                let allDownloadsSuccessful = true;

                for (const fileId of fileIds) {

                    const success = await downloadFileFromId(fileId);
                    if (!success) {
                        console.error(`Failed to download file ${fileId}.`);
                        allDownloadsSuccessful = false;
                    }
                }

                // Clear downloadedFileIds from localStorage after successful downloads
                if (allDownloadsSuccessful) {
                    localStorage.removeItem('downloadedFileIds');
                    // Update the state to hide the button
                    setFileIds([]);
                }
            } catch (error) {
                console.error('Error during download:', error);
            }
        };

        if (fileIds.length === 0) {
            return null;
        }

        return (
            <div className="message-block assistant message-block-container" style={{ marginTop: '20px' }}>
                <div className="assistant-message assistant-message-container">
                    <div className="assistant-content">
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '0px 0'
                        }}>
                            <button
                                onClick={handleDownload}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#1e3abf';
                                    e.target.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#2241DD';
                                    e.target.style.transform = 'translateY(0px)';
                                }}
                                style={{
                                    backgroundColor: '#2241DD',
                                    border: 'none',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    padding: '12px 20px',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(34, 65, 221, 0.3)',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7,10 12,15 17,10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                Download ({fileIds.length})
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // When removing the file (manually or via new chat), clear the file input value as well
    const clearUploadedFile = () => {
        setUploadedFile(null);
        const fileInput = document.getElementById('fileUpload');
        if (fileInput) fileInput.value = '';
    };

    useEffect(() => {
        const clearFileListener = () => clearUploadedFile();
        window.addEventListener('clearFileInput', clearFileListener);
        return () => window.removeEventListener('clearFileInput', clearFileListener);
    }, []);

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles && acceptedFiles.length > 0) {
            // Simulate a file input change event
            handleFileChange({ target: { files: acceptedFiles } });
        }
    }, []);
    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        noClick: true,
        noKeyboard: true,
        multiple: false,
    });

    // Function to render user message content with preserved line breaks
    const renderUserMessageContent = (content) => {
        if (typeof content === 'object') {
            if (content.text) {
                return content.text.split('\n').map((line, index) => (
                    <React.Fragment key={index}>
                        {line}
                        {index < content.text.split('\n').length - 1 && <br />}
                    </React.Fragment>
                ));
            }
            return content;
        } else if (typeof content === 'string') {
            return content.split('\n').map((line, index) => (
                <React.Fragment key={index}>
                    {line}
                    {index < content.split('\n').length - 1 && <br />}
                </React.Fragment>
            ));
        }
        return content;
    };

    return (
        <>
            <MathJaxContext version={3.2} config={config}>
                <main
                    className="chat-main chat-main-container"
                >
                    <div className="chat-header">
                        {(!isGraphingMode && !isToolsMode) && (
                            <button
                                className={`prompt-guide-btn prompt-button ${!isAuthenticated ? 'not-auth' : ''}`}
                                onClick={handleOpenPromptGuide}
                                title="Prompt Guide"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                </svg>
                                Prompt Guide
                            </button>
                        )}
                        {isAuthenticated && (
                            <button
                                className="feedback-btn feedback-button"
                                onClick={handleOpenFeedback}
                                title="Feedback"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                                    <path d="M8 9h8"></path>
                                    <path d="M8 13h6"></path>
                                </svg>
                                Feedback
                            </button>
                        )}
                        {isAuthenticated && (
                            <Link
                                to="/status"
                                className="status-btn status-button"
                                title={`OpenAI API Status: ${getStatusText(apiStatus?.indicator)}`}
                            >
                                <div className="status-indicator-container">
                                    <div
                                        className="status-dot"
                                        style={{
                                            backgroundColor: statusLoading ? '#6b7280' : getStatusColor(apiStatus?.indicator),
                                            animation: statusLoading ? 'pulse 2s infinite' : 'pulse 3s infinite'
                                        }}
                                    ></div>
                                    <svg
                                        className={`status-icon ${getStatusTextClass(apiStatus?.indicator)}`}
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round">
                                        <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                                    </svg>
                                    <span className={`status-text ${getStatusTextClass(apiStatus?.indicator)}`}>
                                        {statusLoading ? 'Loading...' : getStatusText(apiStatus?.indicator)}
                                    </span>
                                </div>
                            </Link>
                        )}

                        {!isAuthenticated && (
                            <div className="logo-container">
                                <Link to="/">
                                    <img
                                        src={Geohublogo}
                                        alt="GeoHub Logo"
                                        className="logo-image"
                                    />
                                </Link>
                            </div>
                        )}
                        {!isAuthenticated && (
                            <button
                                className="login-btn login-btn-header"
                                onClick={() => {
                                    sessionStorage.setItem('fromChatLimit', 'true');
                                    window.location.href = '/Login';
                                }}
                                title="Login"
                            >
                                Login
                            </button>
                        )}

                    </div>
                    {isToolsMode ? (
                        <Tools />
                    ) : currentConversation.length > 0 ? (
                        <div className="messages-container messages-container-custom"
                            ref={messagesContainerRef}
                            onScroll={handleScroll}
                        >
                            {currentConversation.map((message, index) => (
                                <div key={index} className={`message-block ${message.role} message-block-container`}>
                                    {message.role === 'user' ? (
                                        <div className="user-message">
                                            <h3 className="user-heading">
                                                <span className="user-icon">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                                        <circle cx="12" cy="7" r="4"></circle>
                                                    </svg>
                                                </span>
                                                {editingMessageId === message.id ? (
                                                    <div className="edit-message-container">
                                                        <textarea
                                                            value={editValue}
                                                            onChange={(e) => setEditValue(e.target.value)}
                                                            className="edit-message-textarea"
                                                            autoFocus
                                                        />
                                                        <div className="edit-buttons">
                                                            <button
                                                                onClick={() => submitEdit(message.id)}
                                                                className="edit-submit-btn"
                                                            >
                                                                Send
                                                            </button>
                                                            <button
                                                                onClick={cancelEditing}
                                                                className="edit-cancel-btn"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        {renderUserMessageContent(message.content)}
                                                        <button
                                                            onClick={() => startEditing(message)}
                                                            className="edit-button"
                                                            title="Edit message"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                            </svg>
                                                        </button>
                                                    </>
                                                )}
                                            </h3>

                                            {typeof message.content === 'object' && message.content.image && (
                                                <div className="user-image">
                                                    <img
                                                        src={message.content.image}
                                                        alt="User uploaded"
                                                        onClick={() => window.open(message.content.image, '_blank')}
                                                    />
                                                </div>
                                            )}

                                            {typeof message.content === 'object' && message.content.csvData && (
                                                <div className="user-csv">
                                                    {csvToHtmlTable(message.content.csvData, message.content.fileName)}
                                                </div>
                                            )}

                                            {typeof message.content === 'object' && message.content.txtData && (
                                                <div className="user-txt">
                                                    <div className="txt-preview">
                                                        <div className="txt-file-info">
                                                            <span className="txt-file-icon">📄</span>
                                                            <span className="txt-file-name">{message.content.fileName || 'TXT File'}</span>
                                                        </div>
                                                        <div className="txt-content">
                                                            <pre style={{
                                                                whiteSpace: 'pre-wrap',
                                                                wordBreak: 'break-word',
                                                                maxHeight: '200px',
                                                                overflow: 'auto',
                                                                padding: '10px',
                                                                backgroundColor: '#f5f5f5',
                                                                border: '1px solid #ddd',
                                                                borderRadius: '4px',
                                                                fontSize: '14px'
                                                            }}>
                                                                {message.content.txtData.substring(0, 500)}
                                                                {message.content.txtData.length > 500 && '...'}
                                                            </pre>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : message.role === 'assistant' ? (
                                        <div className="assistant-message assistant-message-container">
                                            {/* Copy icon button for text/markdown responses only */}
                                            {message.content && !(typeof message.content === 'object' && message.content.type === 'chart') && (
                                                <button
                                                    className="copy-btn copy-btn-mobile"
                                                    onClick={() => handleCopyResponse(message)}
                                                    title="Copy response"
                                                >
                                                    {Icons.copy}
                                                    {copiedMessageId === message.id && (
                                                        <span className="copy-tooltip">
                                                            Copied!
                                                        </span>
                                                    )}
                                                </button>
                                            )}
                                            <div className="assistant-content">
                                                {typeof message.content === 'object' && message.content.type === 'chart' ? (
                                                    <div className="chart-container image-item1">
                                                        <div className="chart-title">{message.content.title}</div>
                                                        <div className="chart-message">
                                                            <ChartRenderer chartConfig={message.content.chartConfig} />
                                                        </div>
                                                        <div className="chart-info">
                                                            <div className="chart-type">Chart Type: {message.content.chartType}</div>
                                                            <div className="chart-metrics">Series: {message.content.series}</div>
                                                            <div className="chart-data-count">Data Points: {message.content.dataPoints}</div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        {!message.content && (message.functionCall || message.searchStatus) && (
                                                            <div className="assistant-text-container">
                                                                <FunctionCallStatus
                                                                    functionCall={message.functionCall}
                                                                    searchStatus={message.searchStatus}
                                                                />
                                                            </div>
                                                        )}
                                                        {message.content && (
                                                            <MarkdownWithMath
                                                                content={
                                                                    typeof message.content === 'string'
                                                                        ? message.content.trim()
                                                                        : typeof message.content === 'object'
                                                                            ? JSON.stringify(message.content)
                                                                            : String(message.content)
                                                                }
                                                                remarkPlugins={[remarkGfm]}
                                                                components={{
                                                                    h1: ({ node, ...props }) => <h1 className="markdown-h1" {...props} />,
                                                                    h2: ({ node, ...props }) => <h2 className="markdown-h2" {...props} />,
                                                                    h3: ({ node, ...props }) => <h3 className="markdown-h3" {...props} />,
                                                                    h4: ({ node, ...props }) => <h4 className="markdown-h4" {...props} />,
                                                                    p: ({ node, ...props }) => <p className="markdown-p" {...props} />,
                                                                    ul: ({ node, ...props }) => <ul className="markdown-ul" {...props} />,
                                                                    ol: ({ node, ...props }) => <ol className="markdown-ol" {...props} />,
                                                                    li: ({ node, ...props }) => <li className="markdown-li" {...props} />,
                                                                    blockquote: ({ node, ...props }) => <blockquote className="markdown-blockquote" {...props} />,
                                                                    table: ({ node, ...props }) => (
                                                                        <div className="markdown-table-container">
                                                                            <table className="markdown-table" {...props} />
                                                                        </div>
                                                                    ),
                                                                    th: ({ node, ...props }) => <th className="markdown-th" {...props} />,
                                                                    td: ({ node, ...props }) => <td className="markdown-td" {...props} />,
                                                                    a: ({ node, href, ...props }) => (
                                                                        <a
                                                                            href={href}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="markdown-link"
                                                                            {...props}
                                                                        />
                                                                    ),
                                                                }}
                                                            />
                                                        )}
                                                        {message.images && message.images.length > 0 && (
                                                            <div className="assistant-images">
                                                                {(() => {
                                                                    console.log('🖼️ Rendering images:', message.images.length, message.images);
                                                                    return message.images.map((image, imgIndex) => {
                                                                        // Detect if this image is a graph/chart/visualization
                                                                        const isGraphImage =
                                                                            (image.isGraph === true) ||
                                                                            (image.type === 'graph') ||
                                                                            (image.name && /chart|graph|visualization/i.test(image.name)) ||
                                                                            (image.url && /chart|graph|visualization/i.test(image.url));

                                                                        console.log(`🖼️ Rendering images for graph ${imgIndex}:`, {
                                                                            url: image.url?.substring(0, 50) + '...',
                                                                            name: image.name,
                                                                            hasUrl: !!image.url
                                                                        });

                                                                        return (
                                                                            <div key={imgIndex} className={`image-item${isGraphImage ? ' image-item1' : ''}`}>
                                                                                <img
                                                                                    src={image.url}
                                                                                    alt={image.name || `Image ${imgIndex + 1}`}
                                                                                    className={isGraphImage ? "large-assistant-image" : "assistant-image"}
                                                                                    onClick={() => window.open(image.url, '_blank')}
                                                                                    onLoad={() => {
                                                                                        console.log(`🖼️ ✅ Image ${imgIndex} loaded successfully:`, image.name);
                                                                                    }}
                                                                                    onError={(e) => {
                                                                                        console.error(`🖼️ ❌ Image ${imgIndex} failed to load:`, {
                                                                                            url: image.url?.substring(0, 100),
                                                                                            name: image.name,
                                                                                            error: e.type,
                                                                                            target: e.target
                                                                                        });
                                                                                    }}
                                                                                />
                                                                                {image.name && (
                                                                                    <div className="image-caption">{image.name}</div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    });
                                                                })()}
                                                            </div>
                                                        )}
                                                        {isLoading && index === currentConversation.length - 1 &&
                                                            !message.functionCall && !message.searchStatus && (
                                                                <TypingLoader />
                                                            )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="error-message">
                                            <div className="error-icon">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="10"></circle>
                                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                                </svg>
                                            </div>
                                            <div className="error-content">{message.content}</div>
                                        </div>
                                    )}
                                </div>
                            ))}
                            <DownloadButton />
                        </div>
                    ) : (
                        <div className="flex-1-3-auto"></div>
                    )}

                    {!isToolsMode && (
                        <div className="input-area">
                            <div
                                className="textarea-container"
                                {...getRootProps()}
                                onClick={() => document.getElementById("userTextarea").focus()}
                            >
                                {/* <button
                                    type="button"
                                    className={`mic-btn ${isListening ? 'listening' : 'not-listening'}`}
                                    onClick={() => {
                                        if (isAuthenticated) handleMicClick();
                                    }}
                                    title={
                                        isAuthenticated
                                            ? (isListening ? "Stop recording" : "Voice input")
                                            : "Please login to use this feature"
                                    }
                                    disabled={!isAuthenticated}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke={isListening ? "white" : "currentColor"}
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                                        <line x1="12" y1="19" x2="12" y2="22" />
                                    </svg>
                                </button> */}

                                <textarea
                                    id="userTextarea"
                                    className="user-textarea"
                                    value={userInput}
                                    onChange={(e) => {
                                        setUserInput(e.target.value);
                                        e.target.style.height = "auto";
                                        e.target.style.height = `${Math.min(e.target.scrollHeight, 100)}px`;
                                    }}
                                    onKeyDown={(e) => {
                                        handleKeyDown(e);
                                        if (e.key === "Enter" || e.key === "Backspace" || e.key === "Delete") {
                                            e.target.style.height = "auto";
                                            e.target.style.height = `${Math.min(e.target.scrollHeight, 100)}px`;
                                        }
                                    }}
                                    onFocus={(e) => {
                                        setIsTyping(true);
                                        e.target.style.height = "auto";
                                        e.target.style.height = `${Math.min(e.target.scrollHeight, 100)}px`;
                                    }}
                                    onBlur={() => !userInput.trim() && setIsTyping(false)}
                                    placeholder={isChartAnalysisMode ? "Ask a question about the chart..." : "Type your message here..."}
                                    disabled={isLoading || isUploading}
                                />
                                {uploadedFile && (
                                    <div className="image-preview-card">
                                        {uploadedFile.type === 'image' && (
                                            <img
                                                src={uploadedFile.url}
                                                alt="Preview"
                                                className="image-preview"
                                            />
                                        )}
                                        {uploadedFile.type === 'pdf' && (
                                            <div className="pdf-preview">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pdf-icon">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                    <polyline points="14 2 14 8 20 8"></polyline>
                                                    <line x1="16" y1="13" x2="8" y2="13"></line>
                                                    <line x1="16" y1="17" x2="8" y2="17"></line>
                                                    <polyline points="10 9 9 9 8 9"></polyline>
                                                </svg>
                                                <span className="pdf-filename">{uploadedFile.file_name}</span>
                                            </div>
                                        )}
                                        {uploadedFile.type === 'csv' && (
                                            <div className="pdf-preview">
                                                <svg xmlns="http://www.w3.org/2000/svg"
                                                    width="24" height="24" viewBox="0 0 24 24"
                                                    fill="none" stroke="green" strokeWidth="2"
                                                    strokeLinecap="round" strokeLinejoin="round"
                                                    className="pdf-icon">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                    <polyline points="14 2 14 8 20 8"></polyline>
                                                    <line x1="16" y1="13" x2="8" y2="13"></line>
                                                    <line x1="16" y1="17" x2="8" y2="17"></line>
                                                    <polyline points="10 9 9 9 8 9"></polyline>
                                                </svg>
                                                <span className="pdf-filename">{uploadedFile.file_name}</span>
                                            </div>
                                        )}
                                        {uploadedFile.type === 'txt' && (
                                            <div className="pdf-preview">
                                                <svg xmlns="http://www.w3.org/2000/svg"
                                                    width="24" height="24" viewBox="0 0 24 24"
                                                    fill="none" stroke="blue" strokeWidth="2"
                                                    strokeLinecap="round" strokeLinejoin="round"
                                                    className="pdf-icon">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                    <polyline points="14 2 14 8 20 8"></polyline>
                                                    <line x1="16" y1="13" x2="8" y2="13"></line>
                                                    <line x1="16" y1="17" x2="8" y2="17"></line>
                                                    <polyline points="10 9 9 9 8 9"></polyline>
                                                </svg>
                                                <span className="pdf-filename">{uploadedFile.file_name}</span>
                                            </div>
                                        )}
                                        {uploadedFile.type === 'docx' && (
                                            <div className="pdf-preview">
                                                <svg xmlns="http://www.w3.org/2000/svg"
                                                    width="24" height="24" viewBox="0 0 24 24"
                                                    fill="none" stroke="purple" strokeWidth="2"
                                                    strokeLinecap="round" strokeLinejoin="round"
                                                    className="pdf-icon">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                    <polyline points="14 2 14 8 20 8"></polyline>
                                                    <line x1="16" y1="13" x2="8" y2="13"></line>
                                                    <line x1="16" y1="17" x2="8" y2="17"></line>
                                                    <polyline points="10 9 9 9 8 9"></polyline>
                                                </svg>
                                                <span className="pdf-filename">{uploadedFile.file_name}</span>
                                            </div>
                                        )}
                                        <button
                                            onClick={clearUploadedFile}
                                            className="remove-file-btn"
                                            title="Remove image"
                                        >×</button>
                                    </div>
                                )}
                                <div className="input-buttons">
                                    <button
                                        type="button"
                                        className="upload-btn"
                                        onClick={e => {
                                            e.stopPropagation();
                                            if (isAuthenticated) document.getElementById("fileUpload").click();
                                        }}
                                        disabled={isLoading || isUploading || !isAuthenticated}
                                        title={isAuthenticated ? "Attach file" : "Please login to use this feature"}
                                    >
                                        {isUploading ? (
                                            <div className="loader"></div>
                                        ) : (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                strokeWidth="1.5"
                                                stroke="currentColor"
                                                className="w-6 h-6"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.122 2.122l7.81-7.81"
                                                />
                                            </svg>
                                        )}
                                    </button>
                                    <input
                                        {...getInputProps()}
                                        type="file"
                                        id="fileUpload"
                                        className="file-input-hidden"
                                        onChange={handleFileChange}
                                        accept={isGraphingMode ? ".csv" : "image/*,.pdf,.csv,.txt,.docx"}
                                        style={{ display: 'none' }}
                                    />
                                    {(isLoading || isStreaming) ? (
                                        <button
                                            type="button"
                                            className="stop-btn"
                                            onClick={handleStopCommand}
                                            title="Stop generating"
                                        >
                                            <svg width="32" height="32" viewBox="0 0 32 32">
                                                <circle cx="16" cy="16" r="16" fill="#2241DD" />
                                                <rect x="10" y="10" width="12" height="12" rx="2" fill="white" />
                                            </svg>
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="send-btn"
                                            onClick={handleSendMessage}
                                            disabled={!userInput.trim() || isLoading || isUploading}
                                            title="Send message"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                strokeWidth="1.5"
                                                stroke="currentColor"
                                                className="w-6 h-6" >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M4.5 10.5 12 3m0 0 7.5 7.5M12 3v18"
                                                />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {currentConversation.length === 0 && showPrompt && !isSupportMode && (
                        <>
                            <div className="text-center mb-4 px-4">
                                <div className="htxt htxt1">
                                    {isAuthenticated ?
                                        <img src={Geohublogo} alt="GeoHub Logo" className="mobile-logo" /> : ""}
                                    <div className="slogan slogan1">
                                        What would you like to learn about{' '}
                                        <span className="typewriter-text">
                                            {typewriterText}
                                            <span className="typewriter-cursor">|</span>
                                        </span>

                                    </div>
                                </div>
                            </div>
                            {/* {isAuthenticated ? (
                                <div className="flex justify-center py-1">
                                    <ul className="prompt-buttons-list">
                                        {buttons.map((btn, index) => (
                                            <li key={index}></li>
                                        ))}
                                    </ul>
                                </div>
                            ) : null} */}
                        </>
                    )}

                    {currentConversation.length === 0 && isSupportMode && (
                        <div className="text-center mb-4 px-4">
                            <div className="htxt htxt1">
                                <span>Support Bot</span>
                            </div>
                        </div>
                    )}

                    {currentConversation.length === 0 && isGraphingMode && (
                        <div className="text-center mb-4 px-4">
                            <div className="htxt htxt1">
                                <span>{isChartAnalysisMode ? "Chart Analysis Mode" : "Graphing Bot"}</span>
                            </div>
                            {isChartAnalysisMode && (
                                <div className="text-sm text-gray-600 mt-2">
                                    Ask questions about the chart to analyze the data further.
                                </div>
                            )}
                        </div>
                    )}

                    {currentConversation.length === 0 && isToolsMode && (
                        <div className="text-center mb-4 px-4">
                            <div className="htxt htxt1">
                                <span>Tools</span>
                            </div>
                        </div>
                    )}

                    {currentConversation.length === 0 && <div className="flex-1-1-auto"></div>}

                    <MicrophoneModal
                        isOpen={isMicModalOpen}
                        onClose={handleCloseMicModal}
                        isListening={isListening}
                        transcriptionText={transcriptionText}
                    />

                    <div id="image-hover-preview" className="image-hover-preview">
                        {uploadedFile && (
                            <>
                                <div className="image-hover-header">
                                    <button
                                        onClick={() => {
                                            const preview = document.getElementById('image-hover-preview');
                                            if (preview) preview.style.display = 'none';
                                        }}
                                        className="image-hover-close"
                                    >
                                        ×
                                    </button>
                                </div>
                                <div className="image-hover-content">
                                    <img src={uploadedFile.url} alt="Preview" className="image-hover-img" />
                                </div>
                            </>
                        )}
                    </div>

                    <div className="disclosure-text">
                        Help us improve Geohub, by sharing your feedback on its responses.
                        By messaging Geohub, you acknowledge it is an AI that can make mistakes  agree to our <a href="https://www.encardio.com/privacy-policies" target="_blank" rel="noopener noreferrer" className="disclosure-link">Terms and Privacy Policy.</a>
                    </div>
                </main>
            </MathJaxContext>
            {showDisclaimerPopup && (
                <Disclaimer
                    onUnderstand={handleDisclaimerUnderstand}
                    onClose={handleDisclaimerClose}
                    onShowPromptGuide={handleOpenPromptGuide}
                />
            )}

            <ChatLimitPopup
                isOpen={showChatLimitPopup}
                onClose={handleCloseChatLimitPopup}
            />

            <PromptGuideModal
                isPromptGuideOpen={isPromptGuideOpen}
                handleClosePromptGuide={handleClosePromptGuide}
                handleStartConversation={handleStartConversation}
                currentBotType={getCurrentBotType()}
            />
        </>
    );
};

export default Chatconversation;